/* eslint-disable @typescript-eslint/ban-ts-comment */
import { ThreeEvent } from '@react-three/fiber';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { Vector2, Vector3 } from 'three';

import { SnapLineProps } from '../@types/Components';
import { EWall, EWindingOrder } from '../@types/Enums';
import { IBoundaryEdge } from '../models/abstract/BoundaryEdge';
import { IWall } from '../models/abstract/Wall';
import { BoundaryEdge } from '../models/standard/PhysicalBoundary';
import PreviewWallModel, { PreviewWallGeometry } from '../models/standard/PreviewWallModel';
import StandardWallModel, { IStandardWall } from '../models/standard/StandardWallModel';

import { useBuilding } from '../provider/BuildingProvider/BuildingProvider';
import { useMultiSelectToggleContext } from '../provider/MultiSelectToggleProvider';
import { usePlan } from '../provider/PlanProvider';
import { SnackbarType, useSnackbar } from '../provider/SnackbarProvider';
import { useUnitContext } from '../provider/UnitProvider';
import { useValidation } from '../provider/ValidationProvider';

import { fetchAttributesForObject, updateWallGeometryAttributes } from '../utils/attributes/attributesForObjectLoader';
import { ObjectType } from '../utils/attributes/defaultObjectsAndAttributes';
import { calculateBoundaryIndicatorSegments, calculateBoundaryPolygonOrientation, orderBoundaryEdges } from '../utils/boundaryGeometryUtils';
import { DEFAULT_WALL_DEPTH, HALF_CIRCLE_DEGREES, QUARTER_CIRCLE_DEGREES, ROOM_COMPLETION_TOLERANCE, SNAP_ANGLE_THRESHOLD } from '../utils/constants';
import { getFirstWallStartSnapPoint, getSnappedPoint } from '../utils/snappingUtils';
import { calculateWallShape, calculateWindingOrder, reorderWindingOrderToClockwise } from '../utils/vectorUtils';

import { useDoubleClickDetection } from './useDoubleClickDetection';
import { EHistoryAction } from './useUndoRedo';

const EPS_I = 1e-4;
const ZERO_LENGTH_EPS = 1e-6;
const DOCK_POINT_TOLERANCE = (DEFAULT_WALL_DEPTH / 2) * Math.SQRT2 + 1e-3;
const MAX_PROJECTED_PREVIEW_CLOSURE_DISTANCE = 50;

type P2 = { x: number; y: number };
type SnapKind = 'none' | 'chainStart' | 'external';

type SnapInfo = {
  kind: SnapKind;
  point: Vector3 | null;
  dockCenter?: Vector3 | null;
};

type SolvedPreviewSegment = {
  labelStart: Vector3;
  labelEnd: Vector3;
  centerStart: Vector3;
  centerEnd: Vector3;
};

type SolvedEntry = {
  preview: PreviewWallModel;
  structuralStart: Vector3;
  structuralEnd: Vector3;
  labelStart: Vector3;
  labelEnd: Vector3;
  centerStart: Vector3;
  centerEnd: Vector3;
  startDockCenter: Vector3 | null;
  endDockCenter: Vector3 | null;
  startLockedToDock: boolean;
  endLockedToDock: boolean;
};

type ExistingInnerHit = {
  point: Vector3;
  distance: number;
  wall: StandardWallModel;
};

type InsetLine = {
  start: Vector3;
  end: Vector3;
};

type DisplaySegment = {
  start: Vector3;
  end: Vector3;
};

type PreviewOuterDisplay = {
  geometry: PreviewWallGeometry;
  centerStart: Vector3;
  centerEnd: Vector3;
  outerStart: Vector3;
  outerEnd: Vector3;
};

type SimulatedFinalPreviewGeometry = {
  rawWindingOrder: EWindingOrder;
  polygonOrientation: number;
  insetSegmentsByUuid: Map<string, DisplaySegment>;
  outerDisplaysByUuid: Map<string, PreviewOuterDisplay>;
};

type OrderedStandardEdge = {
  wall: IStandardWall;
  reversed: boolean;
};

type ProjectedExistingClosure = {
  dock: Vector3;
  path: StandardWallModel[];
};

type LoopClosedHandler = (loopWalls: IStandardWall[]) => void;

const p2 = (v: Vector3): P2 => ({ x: v.x, y: v.y });

const eqPt = (a: P2, b: P2, eps = EPS_I) => Math.abs(a.x - b.x) <= eps && Math.abs(a.y - b.y) <= eps;

const cross = (a: P2, b: P2, c: P2) => (b.x - a.x) * (c.y - a.y) - (b.y - a.y) * (c.x - a.x);

const onSegment = (a: P2, b: P2, p: P2, eps = EPS_I) => {
  if (Math.abs(cross(a, b, p)) > eps) return false;
  return (p.x - a.x) * (p.x - b.x) + (p.y - a.y) * (p.y - b.y) <= eps;
};

const bboxOverlap = (a: P2, b: P2, c: P2, d: P2, eps = EPS_I) => {
  const minAx = Math.min(a.x, b.x);
  const maxAx = Math.max(a.x, b.x);
  const minAy = Math.min(a.y, b.y);
  const maxAy = Math.max(a.y, b.y);
  const minCx = Math.min(c.x, d.x);
  const maxCx = Math.max(c.x, d.x);
  const minCy = Math.min(c.y, d.y);
  const maxCy = Math.max(c.y, d.y);

  return !(maxAx + eps < minCx || maxCx + eps < minAx || maxAy + eps < minCy || maxCy + eps < minAy);
};

const segmentsIntersectInclusive = (a: P2, b: P2, c: P2, d: P2, eps = EPS_I) => {
  if (!bboxOverlap(a, b, c, d, eps)) return false;

  const o1 = cross(a, b, c);
  const o2 = cross(a, b, d);
  const o3 = cross(c, d, a);
  const o4 = cross(c, d, b);

  const s1 = Math.abs(o1) <= eps ? 0 : Math.sign(o1);
  const s2 = Math.abs(o2) <= eps ? 0 : Math.sign(o2);
  const s3 = Math.abs(o3) <= eps ? 0 : Math.sign(o3);
  const s4 = Math.abs(o4) <= eps ? 0 : Math.sign(o4);

  if (s1 !== s2 && s3 !== s4) return true;

  if (s1 === 0 && onSegment(a, b, c, eps)) return true;
  if (s2 === 0 && onSegment(a, b, d, eps)) return true;
  if (s3 === 0 && onSegment(c, d, a, eps)) return true;
  if (s4 === 0 && onSegment(c, d, b, eps)) return true;

  return false;
};

const colinearOverlapLength = (a: P2, b: P2, c: P2, d: P2) => {
  const dx = Math.abs(b.x - a.x);
  const dy = Math.abs(b.y - a.y);
  const useX = dx >= dy;

  const a0 = useX ? a.x : a.y;
  const a1 = useX ? b.x : b.y;
  const c0 = useX ? c.x : c.y;
  const c1 = useX ? d.x : d.y;

  const min1 = Math.min(a0, a1);
  const max1 = Math.max(a0, a1);
  const min2 = Math.min(c0, c1);
  const max2 = Math.max(c0, c1);

  return Math.min(max1, max2) - Math.max(min1, min2);
};

const segmentsConflict = (A: Vector3, B: Vector3, C: Vector3, D: Vector3) => {
  const a = p2(A);
  const b = p2(B);
  const c = p2(C);
  const d = p2(D);

  if (eqPt(a, b) || eqPt(c, d)) return false;
  if (!segmentsIntersectInclusive(a, b, c, d)) return false;

  const sharedEndpoint = eqPt(a, c) || eqPt(a, d) || eqPt(b, c) || eqPt(b, d);

  if (Math.abs(cross(a, b, c)) <= EPS_I && Math.abs(cross(a, b, d)) <= EPS_I) {
    return colinearOverlapLength(a, b, c, d) > EPS_I;
  }

  if (sharedEndpoint) return false;

  if (onSegment(c, d, a) || onSegment(c, d, b) || onSegment(a, b, c) || onSegment(a, b, d)) {
    return true;
  }

  return true;
};

const isCandidateValidAgainstExisting = (existingWalls: IWall[], candidate: IStandardWall) => {
  const std = existingWalls.filter((w) => w.type === EWall.STANDARD) as IStandardWall[];

  for (const w of std) {
    if (w.uuid === candidate.uuid) continue;

    if (segmentsConflict(candidate.startPoint, candidate.endPoint, w.startPoint, w.endPoint)) {
      return false;
    }
  }

  return true;
};

function findPathOverExistingWalls(
  startPt: Vector3,
  endPt: Vector3,
  existingWalls: IWall[],
  incomingStartPt: Vector3,
  drawnPathPoints?: Vector3[],
): StandardWallModel[] | null {
  const stdWalls = existingWalls.filter((w) => w.type === EWall.STANDARD) as StandardWallModel[];

  function traceFace(preferLeft: boolean): StandardWallModel[] | null {
    const TOLERANCE = ROOM_COMPLETION_TOLERANCE;
    let currentPt = startPt.clone();

    let vIn = startPt.clone().sub(incomingStartPt);
    if (vIn.lengthSq() < 1e-6) {
      vIn = new Vector3(1, 0, 0);
    } else {
      vIn.normalize();
    }

    const path: StandardWallModel[] = [];
    const visited = new Set<string>();

    for (let step = 0; step < 200; step++) {
      if (currentPt.distanceTo(endPt) <= TOLERANCE) return path;

      const candidates = [];

      for (const w of stdWalls) {
        if (visited.has(w.uuid)) continue;

        let nextPt: Vector3 | null = null;
        if (w.startPoint.distanceTo(currentPt) <= TOLERANCE) nextPt = w.endPoint.clone();
        else if (w.endPoint.distanceTo(currentPt) <= TOLERANCE) nextPt = w.startPoint.clone();

        if (nextPt) {
          const vOut = nextPt.clone().sub(currentPt).normalize();
          const aIn = Math.atan2(vIn.y, vIn.x);
          const aOut = Math.atan2(vOut.y, vOut.x);

          let diff = aOut - aIn;
          while (diff <= -Math.PI) diff += 2 * Math.PI;
          while (diff > Math.PI) diff -= 2 * Math.PI;

          candidates.push({ wall: w, nextPt, vOut, angle: diff });
        }
      }

      if (candidates.length === 0) return null;

      candidates.sort((a, b) => a.angle - b.angle);
      const best = preferLeft ? candidates[candidates.length - 1] : candidates[0];

      visited.add(best.wall.uuid);

      const closingWall = new StandardWallModel(
        currentPt.clone(),
        best.nextPt.clone(),
        best.wall.depth,
        best.wall.windows,
        best.wall.doors,
        best.wall.radiators,
        best.wall.attributes,
        best.wall.uuid,
      );

      path.push(closingWall);

      currentPt = best.nextPt;
      vIn = best.vOut;
    }

    return null;
  }

  const pathLeft = traceFace(true);
  const pathRight = traceFace(false);

  function calcArea(points: Vector3[]) {
    let area = 0;

    for (let i = 0; i < points.length; i++) {
      const p1 = points[i];
      const p2 = points[(i + 1) % points.length];
      area += p1.x * p2.y - p2.x * p1.y;
    }

    return Math.abs(area / 2);
  }

  function calcExistingPathArea(path: StandardWallModel[]) {
    return calcArea([startPt, ...path.map((w) => w.endPoint)]);
  }

  function calcDrawnLoopArea(path: StandardWallModel[]) {
    if (!drawnPathPoints || drawnPathPoints.length < 2) return Infinity;

    const pts = drawnPathPoints.map((point) => point.clone());

    if (!eqPt(p2(pts[pts.length - 1]), p2(startPt))) {
      pts.push(startPt.clone());
    }

    path.forEach((wall) => {
      pts.push(wall.endPoint.clone());
    });

    return calcArea(pts);
  }

  if (pathLeft && pathRight) {
    const leftLoopArea = calcDrawnLoopArea(pathLeft);
    const rightLoopArea = calcDrawnLoopArea(pathRight);

    if (Number.isFinite(leftLoopArea) && Number.isFinite(rightLoopArea) && Math.abs(leftLoopArea - rightLoopArea) > EPS_I) {
      return leftLoopArea < rightLoopArea ? pathLeft : pathRight;
    }

    return calcExistingPathArea(pathLeft) < calcExistingPathArea(pathRight) ? pathLeft : pathRight;
  }
  return pathLeft || pathRight || null;
}

const findProjectedExistingClosure = (
  target: Vector3,
  closureStartPoint: Vector3,
  existingWalls: IWall[],
  incomingStartPoint: Vector3,
  drawnPathPoints: Vector3[],
  depth: number,
): ProjectedExistingClosure | null => {
  const maxAxisMiss = Math.max(ROOM_COMPLETION_TOLERANCE, depth * 0.75);
  const maxDockDistance = Math.max(ROOM_COMPLETION_TOLERANCE * 3, depth * 3);
  let best: { dock: Vector3; path: StandardWallModel[]; score: number } | null = null;

  existingWalls.forEach((wall) => {
    if (!wall || wall.type !== EWall.STANDARD) return;

    [wall.startPoint, wall.endPoint].forEach((candidatePoint) => {
      const candidate = candidatePoint.clone();

      if (candidate.distanceTo(closureStartPoint) <= ROOM_COMPLETION_TOLERANCE) return;

      const dx = Math.abs(candidate.x - target.x);
      const dy = Math.abs(candidate.y - target.y);
      const axisMiss = Math.min(dx, dy);

      if (axisMiss > maxAxisMiss) return;
      if (candidate.distanceTo(target) > maxDockDistance) return;

      const path = findPathOverExistingWalls(candidate, closureStartPoint, existingWalls, incomingStartPoint, [...drawnPathPoints, candidate.clone()]);
      if (!path || path.length === 0) return;

      const pathLength = path.reduce((sum, pathWall) => sum + pathWall.startPoint.distanceTo(pathWall.endPoint), 0);
      const score = axisMiss * 1000 + candidate.distanceTo(target) + pathLength * 0.01;

      if (!best || score < best.score) {
        best = {
          dock: candidate,
          path,
          score,
        };
      }
    });
  });

  return best ? { dock: best.dock.clone(), path: best.path } : null;
};

const cloneAttrs = (attrs: any[]) => attrs.map((a) => Object.assign(Object.create(Object.getPrototypeOf(a)), a));

const lineIntersection3 = (p1: Vector3, p2: Vector3, p3: Vector3, p4: Vector3): Vector3 | null => {
  const x1 = p1.x;
  const y1 = p1.y;
  const x2 = p2.x;
  const y2 = p2.y;
  const x3 = p3.x;
  const y3 = p3.y;
  const x4 = p4.x;
  const y4 = p4.y;

  const den = (x1 - x2) * (y3 - y4) - (y1 - y2) * (x3 - x4);
  if (Math.abs(den) < 1e-6) return null;

  const px = ((x1 * y2 - y1 * x2) * (x3 - x4) - (x1 - x2) * (x3 * y4 - y3 * x4)) / den;
  const py = ((x1 * y2 - y1 * x2) * (y3 - y4) - (y1 - y2) * (x3 * y4 - y3 * x4)) / den;

  return new Vector3(px, py, 0);
};

const getLeftNormal = (start: Vector3, end: Vector3, depth: number): Vector3 => {
  const dir = new Vector2(end.x - start.x, end.y - start.y);

  if (dir.lengthSq() < 1e-9) {
    return new Vector3(0, 0, 0);
  }

  dir.normalize();

  return new Vector3(-dir.y, dir.x, 0).multiplyScalar(depth / 2);
};

const getRightNormal = (start: Vector3, end: Vector3, depth: number): Vector3 => getLeftNormal(start, end, depth).multiplyScalar(-1);

const closestPointOnSegment = (a: Vector3, b: Vector3, p: Vector3): Vector3 => {
  const ab = b.clone().sub(a);
  const lenSq = ab.lengthSq();

  if (lenSq < ZERO_LENGTH_EPS) return a.clone();

  const t = Math.max(0, Math.min(1, p.clone().sub(a).dot(ab) / lenSq));
  return a.clone().addScaledVector(ab, t);
};

const mirrorPointAcrossLine = (point: Vector3, lineStart: Vector3, lineEnd: Vector3): Vector3 => {
  const line = lineEnd.clone().sub(lineStart);
  const lenSq = line.lengthSq();

  if (lenSq < ZERO_LENGTH_EPS) return point.clone();

  const t = point.clone().sub(lineStart).dot(line) / lenSq;
  const projection = lineStart.clone().addScaledVector(line, t);

  return projection.multiplyScalar(2).sub(point);
};

const getAvailableSidePointForExistingBoundary = (point: Vector3, edge: IBoundaryEdge): Vector3 => {
  const wallStart = edge.wall?.startPoint;
  const wallEnd = edge.wall?.endPoint;

  if (!(wallStart instanceof Vector3) || !(wallEnd instanceof Vector3)) return point.clone();

  return mirrorPointAcrossLine(point, wallStart, wallEnd);
};

const setSnapWallSource = (snapWall: StandardWallModel, sourceWall?: IWall | null) => {
  const sourceStart = (sourceWall as any)?.startPoint;
  const sourceEnd = (sourceWall as any)?.endPoint;

  if (sourceStart instanceof Vector3 && sourceEnd instanceof Vector3) {
    (snapWall as any).sourceWallStartPoint = sourceStart.clone();
    (snapWall as any).sourceWallEndPoint = sourceEnd.clone();
  }
};

const findNearestExistingInnerPoint = (target: Vector3, existingInnerSnapWalls: StandardWallModel[]): ExistingInnerHit | null => {
  let bestPoint: Vector3 | null = null;
  let bestWall: StandardWallModel | null = null;
  let bestDistance = Infinity;

  existingInnerSnapWalls.forEach((w) => {
    const maxDistance = Math.max(DOCK_POINT_TOLERANCE * 3, (w.depth ?? DEFAULT_WALL_DEPTH) * 3);
    const projected = closestPointOnSegment(w.startPoint, w.endPoint, target);
    const projectedDistance = projected.distanceTo(target);

    if (projectedDistance <= maxDistance && projectedDistance < bestDistance) {
      bestDistance = projectedDistance;
      bestPoint = projected.clone();
      bestWall = w;
    }

    [w.startPoint, w.endPoint].forEach((p) => {
      const cornerDistance = p.distanceTo(target);

      if (cornerDistance <= maxDistance && cornerDistance < bestDistance) {
        bestDistance = cornerDistance;
        bestPoint = p.clone();
        bestWall = w;
      }
    });
  });

  return bestPoint && bestWall ? { point: bestPoint, distance: bestDistance, wall: bestWall } : null;
};

const findDockInsetIntersectionPoint = (
  insetStart: Vector3,
  insetEnd: Vector3,
  dockCenter: Vector3 | null,
  existingInnerSnapWalls: StandardWallModel[],
  fallback: Vector3,
  dockReferencePoint?: Vector3 | null,
  sourceContinuationWalls: IWall[] = [],
): Vector3 => {
  if (!dockCenter || insetStart.distanceTo(insetEnd) <= ZERO_LENGTH_EPS) return fallback.clone();

  let bestPoint: Vector3 | null = null;
  let bestScore = Infinity;
  const dockReferenceDirection = dockReferencePoint && dockReferencePoint.distanceTo(dockCenter) > ZERO_LENGTH_EPS ? dockReferencePoint.clone().sub(dockCenter).normalize() : null;

  const isCollinearWithInsetLine = (innerWall: StandardWallModel) =>
    Math.abs(cross(p2(insetStart), p2(insetEnd), p2(innerWall.startPoint))) <= EPS_I && Math.abs(cross(p2(insetStart), p2(insetEnd), p2(innerWall.endPoint))) <= EPS_I;
  const sourceDockDirection = (innerWall: StandardWallModel): Vector3 | null => {
    const sourceStart = (innerWall as any).sourceWallStartPoint;
    const sourceEnd = (innerWall as any).sourceWallEndPoint;

    if (!(sourceStart instanceof Vector3) || !(sourceEnd instanceof Vector3)) return null;

    if (sourceStart.distanceTo(dockCenter) <= DOCK_POINT_TOLERANCE) {
      return sourceEnd.clone().sub(sourceStart);
    }

    if (sourceEnd.distanceTo(dockCenter) <= DOCK_POINT_TOLERANCE) {
      return sourceStart.clone().sub(sourceEnd);
    }

    return null;
  };
  const wallDockDirection = (wall: IWall): Vector3 | null => {
    const wallStart = (wall as any)?.startPoint;
    const wallEnd = (wall as any)?.endPoint;

    if (!(wallStart instanceof Vector3) || !(wallEnd instanceof Vector3)) return null;

    if (wallStart.distanceTo(dockCenter) <= DOCK_POINT_TOLERANCE) {
      return wallEnd.clone().sub(wallStart);
    }

    if (wallEnd.distanceTo(dockCenter) <= DOCK_POINT_TOLERANCE) {
      return wallStart.clone().sub(wallEnd);
    }

    return null;
  };
  const hasSourceContinuationFromDock = (direction: Vector3) => {
    if (direction.lengthSq() <= ZERO_LENGTH_EPS) return false;

    const normalizedDirection = direction.clone().normalize();

    return existingInnerSnapWalls.some((innerWall) => {
      const sourceDirection = sourceDockDirection(innerWall);

      if (!sourceDirection || sourceDirection.lengthSq() <= ZERO_LENGTH_EPS) return false;

      return sourceDirection.normalize().dot(normalizedDirection) > 1 - EPS_I;
    });
  };
  const hasOppositeSourceContinuationFromDock = (direction: Vector3) => {
    if (direction.lengthSq() <= ZERO_LENGTH_EPS) return false;

    const normalizedDirection = direction.clone().normalize();

    const hasOppositeSnapWall = existingInnerSnapWalls.some((innerWall) => {
      const sourceDirection = sourceDockDirection(innerWall);

      if (!sourceDirection || sourceDirection.lengthSq() <= ZERO_LENGTH_EPS) return false;

      return sourceDirection.normalize().dot(normalizedDirection) < -1 + EPS_I;
    });

    if (hasOppositeSnapWall) return true;

    return sourceContinuationWalls.some((wall) => {
      const wallDirection = wallDockDirection(wall);

      if (!wallDirection || wallDirection.lengthSq() <= ZERO_LENGTH_EPS) return false;

      return wallDirection.normalize().dot(normalizedDirection) < -1 + EPS_I;
    });
  };
  const isUnsupportedEndpointMiter = (innerWall: StandardWallModel) => {
    const sourceDirection = sourceDockDirection(innerWall);

    if (!dockReferenceDirection || !sourceDirection || sourceDirection.lengthSq() <= ZERO_LENGTH_EPS) return false;
    if (sourceDirection.clone().normalize().dot(dockReferenceDirection) > 0.5) return false;
    if (hasOppositeSourceContinuationFromDock(sourceDirection)) return false;

    const fallbackDirection = fallback.clone().sub(dockCenter);
    if (fallbackDirection.lengthSq() <= ZERO_LENGTH_EPS) return false;

    const fallbackSide = crossVectors2D(dockReferenceDirection, fallbackDirection);
    const sourceSide = crossVectors2D(dockReferenceDirection, sourceDirection);

    if (Math.abs(fallbackSide) <= EPS_I || Math.abs(sourceSide) <= EPS_I) return false;

    return Math.sign(fallbackSide) !== Math.sign(sourceSide);
  };
  const isUnsupportedSourceMiterExtension = (innerWall: StandardWallModel, intersection: Vector3) => {
    const sourceDirection = sourceDockDirection(innerWall);
    const intersectionDirection = intersection.clone().sub(dockCenter);

    if (!sourceDirection || sourceDirection.lengthSq() <= ZERO_LENGTH_EPS || intersectionDirection.lengthSq() <= ZERO_LENGTH_EPS) return false;

    const normalizedIntersectionDirection = intersectionDirection.clone().normalize();
    const pointsPastSourceDock = sourceDirection.clone().normalize().dot(normalizedIntersectionDirection) < -EPS_I;

    return pointsPastSourceDock && !hasSourceContinuationFromDock(intersectionDirection);
  };
  const sourceDockAlignment = (innerWall: StandardWallModel) => {
    const sourceDirection = sourceDockDirection(innerWall);

    if (!dockReferenceDirection || !sourceDirection || sourceDirection.lengthSq() <= ZERO_LENGTH_EPS) return 0;

    return sourceDirection.normalize().dot(dockReferenceDirection);
  };
  const hasCollinearDockSide = existingInnerSnapWalls.some((innerWall) => {
    if (innerWall.startPoint.distanceTo(innerWall.endPoint) <= ZERO_LENGTH_EPS) return false;

    const nearestToDock = closestPointOnSegment(innerWall.startPoint, innerWall.endPoint, dockCenter);
    const dockDistance = nearestToDock.distanceTo(dockCenter);
    const maxDockDistance = Math.max(DOCK_POINT_TOLERANCE * 3, (innerWall.depth ?? DEFAULT_WALL_DEPTH) * 3);

    return dockDistance <= maxDockDistance && isCollinearWithInsetLine(innerWall);
  });

  existingInnerSnapWalls.forEach((innerWall) => {
    if (innerWall.startPoint.distanceTo(innerWall.endPoint) <= ZERO_LENGTH_EPS) return;

    const nearestToDock = closestPointOnSegment(innerWall.startPoint, innerWall.endPoint, dockCenter);
    const dockDistance = nearestToDock.distanceTo(dockCenter);
    const maxDockDistance = Math.max(DOCK_POINT_TOLERANCE * 3, (innerWall.depth ?? DEFAULT_WALL_DEPTH) * 3);

    if (dockDistance > maxDockDistance) return;

    const intersection = lineIntersection3(insetStart, insetEnd, innerWall.startPoint, innerWall.endPoint);

    if (!intersection || !Number.isFinite(intersection.x) || !Number.isFinite(intersection.y)) return;

    const intersectionToSegment = closestPointOnSegment(innerWall.startPoint, innerWall.endPoint, intersection).distanceTo(intersection);
    if (hasCollinearDockSide && intersectionToSegment > EPS_I) return;
    if (isUnsupportedEndpointMiter(innerWall)) return;
    if (isUnsupportedSourceMiterExtension(innerWall, intersection)) return;

    const alignment = sourceDockAlignment(innerWall);
    const score = intersection.distanceTo(fallback) + dockDistance * 2 + intersectionToSegment * 0.25 - Math.max(alignment - 0.5, 0) * 1000;

    if (score < bestScore) {
      bestScore = score;
      bestPoint = intersection;
    }
  });

  if (bestPoint) return bestPoint.clone();

  const nearestHit = findNearestExistingInnerPoint(dockCenter, existingInnerSnapWalls);
  if (!nearestHit) return fallback.clone();
  if (isCollinearWithInsetLine(nearestHit.wall)) return fallback.clone();

  const intersection = lineIntersection3(insetStart, insetEnd, nearestHit.wall.startPoint, nearestHit.wall.endPoint);

  if (!intersection || !Number.isFinite(intersection.x) || !Number.isFinite(intersection.y)) return fallback.clone();
  if (isUnsupportedEndpointMiter(nearestHit.wall)) return fallback.clone();
  if (isUnsupportedSourceMiterExtension(nearestHit.wall, intersection)) return fallback.clone();

  return intersection;
};

const buildDisplaySegmentsForInsetLines = (
  solved: SolvedEntry[],
  insetLines: InsetLine[],
  existingInnerSnapWalls: StandardWallModel[],
  isCloseEnough: (p1: Vector3, p2: Vector3) => boolean,
  isClosed: boolean,
  sourceContinuationWalls: IWall[] = [],
): DisplaySegment[] => {
  const displaySegments = insetLines.map((line) => ({
    start: line.start.clone(),
    end: line.end.clone(),
  }));

  solved.forEach((entry, index) => {
    const insetLine = insetLines[index];

    if (entry.startDockCenter) {
      displaySegments[index].start = findDockInsetIntersectionPoint(
        insetLine.start,
        insetLine.end,
        entry.startDockCenter,
        existingInnerSnapWalls,
        insetLine.start,
        entry.labelEnd,
        sourceContinuationWalls,
      );
    }

    if (entry.endDockCenter) {
      displaySegments[index].end = findDockInsetIntersectionPoint(
        insetLine.start,
        insetLine.end,
        entry.endDockCenter,
        existingInnerSnapWalls,
        insetLine.end,
        entry.labelStart,
        sourceContinuationWalls,
      );
    }
  });

  for (let i = 1; i < solved.length; i++) {
    const prev = solved[i - 1];
    const curr = solved[i];

    if (!isCloseEnough(prev.labelEnd, curr.labelStart)) continue;

    const inter = lineIntersection3(insetLines[i - 1].start, insetLines[i - 1].end, insetLines[i].start, insetLines[i].end);
    if (!inter) continue;

    displaySegments[i - 1].end = inter.clone();
    displaySegments[i].start = inter.clone();
  }

  if (isClosed) {
    const inter = lineIntersection3(insetLines[solved.length - 1].start, insetLines[solved.length - 1].end, insetLines[0].start, insetLines[0].end);

    if (inter) {
      displaySegments[solved.length - 1].end = inter.clone();
      displaySegments[0].start = inter.clone();
    }
  }

  return displaySegments;
};

const displaySegmentWithCurrentStartAndFinalLength = (current: DisplaySegment, finalSegment: DisplaySegment): DisplaySegment => {
  const directScore = finalSegment.start.distanceTo(current.start) + finalSegment.end.distanceTo(current.end);
  const reversedScore = finalSegment.end.distanceTo(current.start) + finalSegment.start.distanceTo(current.end);
  const finalEnd = directScore <= reversedScore ? finalSegment.end.clone() : finalSegment.start.clone();
  const finalLength = finalSegment.start.distanceTo(finalSegment.end);
  const direction = finalEnd.sub(current.start);

  if (direction.lengthSq() <= ZERO_LENGTH_EPS) {
    return {
      start: current.start.clone(),
      end: current.end.clone(),
    };
  }

  direction.normalize();

  return {
    start: current.start.clone(),
    end: current.start.clone().addScaledVector(direction, finalLength),
  };
};

const midpointOfDisplaySegment = (segment: DisplaySegment) => segment.start.clone().add(segment.end).multiplyScalar(0.5);

const midpointOfOuterDisplay = (display: PreviewOuterDisplay) => display.outerStart.clone().add(display.outerEnd).multiplyScalar(0.5);

const getInnerIndicatorOrientationForDisplay = (
  innerDisplay: DisplaySegment,
  polygonOrientation: number | undefined,
  outerDisplay?: PreviewOuterDisplay | null,
) => {
  if (!polygonOrientation || !outerDisplay) return polygonOrientation;

  const dir = innerDisplay.end.clone().sub(innerDisplay.start);
  if (dir.lengthSq() <= ZERO_LENGTH_EPS) return polygonOrientation;

  dir.normalize();

  const indicatorNormal =
    polygonOrientation === 1
      ? new Vector3(-dir.y, dir.x, 0)
      : new Vector3(dir.y, -dir.x, 0);

  const inwardDirection = midpointOfDisplaySegment(innerDisplay).sub(midpointOfOuterDisplay(outerDisplay));
  if (inwardDirection.lengthSq() <= ZERO_LENGTH_EPS) return polygonOrientation;

  return indicatorNormal.dot(inwardDirection) >= 0 ? polygonOrientation : -polygonOrientation;
};

const getOuterDisplayForIndicator = (display: PreviewOuterDisplay): PreviewOuterDisplay => {
  const dir = display.centerEnd.clone().sub(display.centerStart);
  if (dir.lengthSq() <= ZERO_LENGTH_EPS) return display;

  dir.normalize();

  const labelNormal = new Vector3(-dir.y, dir.x, 0);
  const centerMidpoint = display.centerStart.clone().add(display.centerEnd).multiplyScalar(0.5);
  const outwardDirection = midpointOfOuterDisplay(display).sub(centerMidpoint);

  if (outwardDirection.lengthSq() <= ZERO_LENGTH_EPS || labelNormal.dot(outwardDirection) >= 0) return display;

  return {
    ...display,
    centerStart: display.centerEnd.clone(),
    centerEnd: display.centerStart.clone(),
  };
};

const signedArea2D = (points: Vector3[]) => {
  let area = 0;

  for (let i = 0; i < points.length; i++) {
    const a = points[i];
    const b = points[(i + 1) % points.length];

    area += a.x * b.y - b.x * a.y;
  }

  return area / 2;
};

const crossVectors2D = (a: Vector3, b: Vector3) => a.x * b.y - a.y * b.x;

const areDirectionsParallel2D = (a: Vector3, b: Vector3, eps = EPS_I): boolean => {
  if (a.lengthSq() <= ZERO_LENGTH_EPS || b.lengthSq() <= ZERO_LENGTH_EPS) return false;

  const an = a.clone().normalize();
  const bn = b.clone().normalize();

  return Math.abs(crossVectors2D(an, bn)) <= eps;
};

const projectPointOntoInfiniteLine = (point: Vector3, linePoint: Vector3, lineDirection: Vector3): Vector3 | null => {
  const direction = lineDirection.clone();

  if (direction.lengthSq() <= ZERO_LENGTH_EPS) return null;

  direction.normalize();

  const distance = point.clone().sub(linePoint).dot(direction);

  return linePoint.clone().addScaledVector(direction, distance);
};

const getDockAxisProjectedStart = (start: Vector3, dock: Vector3, axisStart: Vector3, axisEnd: Vector3, depth: number): Vector3 | null => {
  const axis = axisEnd.clone().sub(axisStart);
  const projected = projectPointOntoInfiniteLine(start, dock, axis);

  if (!projected) return null;

  const maxAxisOffset = Math.max(DOCK_POINT_TOLERANCE, depth * 1.25);
  if (projected.distanceTo(start) > maxAxisOffset) return null;

  const projectedDirection = dock.clone().sub(projected);
  if (projectedDirection.lengthSq() <= ZERO_LENGTH_EPS || axis.lengthSq() <= ZERO_LENGTH_EPS) return null;

  projectedDirection.normalize();
  axis.normalize();

  return Math.abs(crossVectors2D(projectedDirection, axis)) <= EPS_I ? projected : null;
};

const arePointsOnOppositeAxisExtensions = (a: Vector3, b: Vector3, axisStart: Vector3, axisEnd: Vector3, margin = DOCK_POINT_TOLERANCE): boolean => {
  const axis = axisEnd.clone().sub(axisStart);
  const axisLength = axis.length();

  if (axisLength <= ZERO_LENGTH_EPS) return false;

  axis.normalize();

  const ta = a.clone().sub(axisStart).dot(axis);
  const tb = b.clone().sub(axisStart).dot(axis);

  return (ta < -margin && tb > axisLength + margin) || (tb < -margin && ta > axisLength + margin);
};

const isSingleDockedPreviewCounterClockwise = (preview: PreviewWallModel, existingWalls: IWall[]): boolean => {
  const startDockCenter = (preview as any).startDockCenter;
  if (!(startDockCenter instanceof Vector3)) return false;

  const previewDirection = preview.structuralEndPoint.clone().sub(preview.structuralStartPoint);
  if (previewDirection.lengthSq() < ZERO_LENGTH_EPS) return false;

  let bestOrientation = 0;
  let bestScore = -Infinity;

  existingWalls.forEach((wall) => {
    if (!wall || wall.type !== EWall.STANDARD) return;

    const startDistance = startDockCenter.distanceTo(wall.startPoint);
    const endDistance = startDockCenter.distanceTo(wall.endPoint);
    const dockedAtStart = startDistance <= DOCK_POINT_TOLERANCE;
    const dockedAtEnd = endDistance <= DOCK_POINT_TOLERANCE;

    if (!dockedAtStart && !dockedAtEnd) return;

    const incomingDirection = dockedAtStart ? wall.startPoint.clone().sub(wall.endPoint) : wall.endPoint.clone().sub(wall.startPoint);
    if (incomingDirection.lengthSq() < ZERO_LENGTH_EPS) return;

    const orientation = crossVectors2D(incomingDirection, previewDirection);
    const endpointDistance = dockedAtStart ? startDistance : endDistance;
    const score = Math.abs(orientation) - endpointDistance * 1e-3;

    if (score > bestScore) {
      bestScore = score;
      bestOrientation = orientation;
    }
  });

  return bestOrientation > EPS_I;
};

const isPreviewFollowingExistingSourceFromStartDock = (preview: PreviewWallModel, existingWalls: IWall[]) => {
  const startDockCenter = (preview as any).startDockCenter;
  if (!(startDockCenter instanceof Vector3)) return false;

  const previewDirection = preview.structuralEndPoint.clone().sub(preview.structuralStartPoint);
  if (previewDirection.lengthSq() < ZERO_LENGTH_EPS) return false;

  previewDirection.normalize();

  return existingWalls.some((wall) => {
    if (!wall || wall.type !== EWall.STANDARD) return false;

    let sourceDirection: Vector3 | null = null;

    if (wall.startPoint.distanceTo(startDockCenter) <= DOCK_POINT_TOLERANCE) {
      sourceDirection = wall.endPoint.clone().sub(wall.startPoint);
    } else if (wall.endPoint.distanceTo(startDockCenter) <= DOCK_POINT_TOLERANCE) {
      sourceDirection = wall.startPoint.clone().sub(wall.endPoint);
    }

    if (!sourceDirection || sourceDirection.lengthSq() <= ZERO_LENGTH_EPS) return false;

    return sourceDirection.normalize().dot(previewDirection) > 0.5;
  });
};

const isCounterClockwisePreviewChain = (previews: PreviewWallModel[], existingWalls: IWall[] = []) => {
  if (previews.length === 1) {
    return isSingleDockedPreviewCounterClockwise(previews[0], existingWalls);
  }

  if (previews.length > 1 && (previews[0] as any).startDockCenter instanceof Vector3 && isPreviewFollowingExistingSourceFromStartDock(previews[0], existingWalls)) {
    return isSingleDockedPreviewCounterClockwise(previews[0], existingWalls);
  }

  if (previews.length < 2) return false;

  const points = previews.map((preview) => preview.labelStartPoint.clone());
  points.push(previews[previews.length - 1].labelEndPoint.clone());

  return signedArea2D(points) > 0;
};

const cloneStandardWallForPreviewSimulation = (input: IStandardWall) =>
  new StandardWallModel(
    input.startPoint.clone(),
    input.endPoint.clone(),
    input.depth,
    [...(input.windows ?? [])],
    [...(input.doors ?? [])],
    [...(input.radiators ?? [])],
    [...(input.attributes ?? [])],
    input.uuid,
  );

const reverseStandardWallInPlaceForPreviewSimulation = (wall: IStandardWall) => {
  const start = wall.startPoint.clone();
  wall.startPoint = wall.endPoint.clone();
  wall.endPoint = start;
  wall.updateWallLengths?.();
};

const existingRoomWallUuidsForPreviewSimulation = (rooms: any[] = []) => {
  const uuids = new Set<string>();

  rooms.forEach((room: any) => {
    (room?.spaces ?? []).forEach((space: any) => {
      (space?.walls ?? []).forEach((wall: any) => {
        if (typeof wall?.uuid === 'string') uuids.add(wall.uuid);
      });
    });
  });

  return uuids;
};

const getUsedSidesForPreviewSimulation = (rooms: any[] = []) => {
  const usedSides = new Map<string, Set<'A' | 'B'>>();

  rooms.forEach((room: any) => {
    (room?.spaces ?? []).forEach((space: any) => {
      (space?.boundaries ?? []).forEach((boundary: any) => {
        (boundary?.edges ?? []).forEach((edge: any) => {
          const uuid = edge?.wall?.uuid;
          const side = edge?.side;

          if (typeof uuid !== 'string') return;
          if (side !== 'A' && side !== 'B') return;

          if (!usedSides.has(uuid)) usedSides.set(uuid, new Set());
          usedSides.get(uuid)!.add(side);
        });
      });
    });
  });

  return usedSides;
};

const normalizePreviewLoopLikeBuilding = (loopWalls: IStandardWall[], rooms: any[] = []) => {
  const cloned = loopWalls.map(cloneStandardWallForPreviewSimulation);
  const sharedWallUuids = existingRoomWallUuidsForPreviewSimulation(rooms);
  const hasSharedWalls = cloned.some((wall) => sharedWallUuids.has(wall.uuid));
  const rawWindingOrder = calculateWindingOrder(cloned);

  if (rawWindingOrder !== EWindingOrder.COUNTERCLOCKWISE) {
    return { normalizedWalls: cloned, rawWindingOrder };
  }

  if (hasSharedWalls) {
    const byUuid = new Map(cloned.map((wall) => [wall.uuid, wall] as const));
    const normalized = cloned.slice().reverse();

    normalized.forEach((wall) => {
      if (sharedWallUuids.has(wall.uuid)) return;

      const original = byUuid.get(wall.uuid);
      if (original) reverseStandardWallInPlaceForPreviewSimulation(original);
    });

    return {
      normalizedWalls: normalized.map((wall) => byUuid.get(wall.uuid) ?? wall),
      rawWindingOrder,
    };
  }

  return {
    normalizedWalls: reorderWindingOrderToClockwise(cloned) as IStandardWall[],
    rawWindingOrder,
  };
};

const isSamePointForPreviewOrdering = (a: Vector3, b: Vector3, eps = ROOM_COMPLETION_TOLERANCE) => Math.abs(a.x - b.x) <= eps && Math.abs(a.y - b.y) <= eps;

const buildContinuousEdgesLikeBuildingForPreview = (loopWalls: IStandardWall[]): OrderedStandardEdge[] => {
  const remaining = loopWalls.slice();
  const first = remaining.shift();
  if (!first) return [];

  const ordered: OrderedStandardEdge[] = [{ wall: first, reversed: false }];
  let cursor = first.endPoint.clone();

  while (remaining.length > 0) {
    let foundIndex = -1;
    let reversed = false;

    for (let i = 0; i < remaining.length; i++) {
      const candidate = remaining[i];

      if (isSamePointForPreviewOrdering(cursor, candidate.startPoint)) {
        foundIndex = i;
        break;
      }

      if (isSamePointForPreviewOrdering(cursor, candidate.endPoint)) {
        foundIndex = i;
        reversed = true;
        break;
      }
    }

    if (foundIndex === -1) break;

    const [picked] = remaining.splice(foundIndex, 1);
    ordered.push({ wall: picked, reversed });
    cursor = reversed ? picked.startPoint.clone() : picked.endPoint.clone();
  }

  return ordered;
};

const weldOrderedChainLikeBuildingForPreview = (ordered: OrderedStandardEdge[]) => {
  const endOf = (entry: OrderedStandardEdge) => (entry.reversed ? entry.wall.startPoint : entry.wall.endPoint);
  const startOf = (entry: OrderedStandardEdge) => (entry.reversed ? entry.wall.endPoint : entry.wall.startPoint);

  ordered.forEach((entry, index) => {
    const next = ordered[(index + 1) % ordered.length];
    const end = endOf(entry);
    const start = startOf(next);

    if (!isSamePointForPreviewOrdering(end, start)) return;

    if (next.reversed) next.wall.endPoint.copy(end);
    else next.wall.startPoint.copy(end);
  });
};

const buildFinalBuildingWallsLikeBuildingForPreview = (loopWalls: IStandardWall[], baseWalls: IWall[], rooms: any[] = []) => {
  const finalBuildingWalls = baseWalls
    .filter(Boolean)
    .filter((wall) => wall.type === EWall.STANDARD)
    .map((wall) => cloneStandardWallForPreviewSimulation(wall as IStandardWall));
  const byUuid = new Map(finalBuildingWalls.map((wall) => [wall.uuid, wall] as const));
  const sharedWallUuids = existingRoomWallUuidsForPreviewSimulation(rooms);
  const rawWindingOrder = calculateWindingOrder(loopWalls);
  let normalizedLoopWalls = loopWalls.slice();

  loopWalls.forEach((wall) => {
    if (byUuid.has(wall.uuid)) return;

    const clone = cloneStandardWallForPreviewSimulation(wall);
    finalBuildingWalls.push(clone);
    byUuid.set(clone.uuid, clone);
  });

  if (rawWindingOrder === EWindingOrder.COUNTERCLOCKWISE) {
    const hasSharedWalls = loopWalls.some((wall) => sharedWallUuids.has(wall.uuid));

    if (hasSharedWalls) {
      normalizedLoopWalls = loopWalls.slice().reverse();

      normalizedLoopWalls.forEach((wall) => {
        if (sharedWallUuids.has(wall.uuid)) return;

        const original = byUuid.get(wall.uuid);
        if (original) reverseStandardWallInPlaceForPreviewSimulation(original);
      });
    } else {
      const reordered = reorderWindingOrderToClockwise(loopWalls.map(cloneStandardWallForPreviewSimulation)) as IStandardWall[];

      reordered.forEach((computed) => {
        const original = byUuid.get(computed.uuid);
        if (!original) return;

        original.startPoint = computed.startPoint.clone();
        original.endPoint = computed.endPoint.clone();
        original.updateWallLengths?.();
      });

      normalizedLoopWalls = reordered;
    }
  }

  const normalizedByUuid = normalizedLoopWalls.map((wall) => byUuid.get(wall.uuid)).filter(Boolean) as IStandardWall[];
  const ordered = buildContinuousEdgesLikeBuildingForPreview(normalizedByUuid);

  if (ordered.length === normalizedByUuid.length) {
    weldOrderedChainLikeBuildingForPreview(ordered);
  }

  return finalBuildingWalls;
};

const buildFinalPreviewOuterDisplays = (loopWalls: IStandardWall[], baseWalls: IWall[], rooms: any[] = []) => {
  const loopUuids = new Set(loopWalls.map((wall) => wall.uuid));
  const finalBuildingWalls = buildFinalBuildingWallsLikeBuildingForPreview(loopWalls, baseWalls, rooms);
  const displays = new Map<string, PreviewOuterDisplay>();

  finalBuildingWalls.forEach((wall) => {
    if (!loopUuids.has(wall.uuid)) return;
    if (wall.startPoint.distanceTo(wall.endPoint) <= ZERO_LENGTH_EPS) return;

    const index = finalBuildingWalls.findIndex((candidate) => candidate.uuid === wall.uuid);
    const geometry = calculateWallShape(wall.startPoint, wall.endPoint, wall.depth, finalBuildingWalls, index);

    displays.set(wall.uuid, {
      geometry,
      centerStart: wall.startPoint.clone(),
      centerEnd: wall.endPoint.clone(),
      outerStart: geometry.pointC.clone(),
      outerEnd: geometry.pointD.clone(),
    });
  });

  return displays;
};

const simulateFinalPreviewGeometry = (loopWalls: IStandardWall[], rooms: any[] = [], baseWalls: IWall[] = []): SimulatedFinalPreviewGeometry | null => {
  if (loopWalls.length < 3) return null;

  const { normalizedWalls, rawWindingOrder } = normalizePreviewLoopLikeBuilding(loopWalls, rooms);
  const ordered = buildContinuousEdgesLikeBuildingForPreview(normalizedWalls);

  if (ordered.length !== normalizedWalls.length) return null;

  weldOrderedChainLikeBuildingForPreview(ordered);

  const usedSides = getUsedSidesForPreviewSimulation(rooms);
  const edges = ordered.map(({ wall, reversed }) => {
    const existingUsage = usedSides.get(wall.uuid);
    const assignedSide: 'A' | 'B' = existingUsage && existingUsage.has('A') && !existingUsage.has('B') ? 'B' : 'A';

    return new BoundaryEdge(wall, reversed ? 1 : 0, reversed ? 0 : 1, assignedSide, reversed);
  });

  const orderedEdges = orderBoundaryEdges(edges);
  const polygonOrientation = calculateBoundaryPolygonOrientation(orderedEdges);
  const insetSegments = calculateBoundaryIndicatorSegments(orderedEdges, polygonOrientation);

  if (insetSegments.length !== orderedEdges.length) return null;

  const insetSegmentsByUuid = new Map<string, DisplaySegment>();

  insetSegments.forEach((segment) => {
    const uuid = segment.edge?.wall?.uuid;
    if (typeof uuid !== 'string') return;

    insetSegmentsByUuid.set(uuid, {
      start: segment.startPoint.clone(),
      end: segment.endPoint.clone(),
    });
  });

  return {
    rawWindingOrder,
    polygonOrientation,
    insetSegmentsByUuid,
    outerDisplaysByUuid: buildFinalPreviewOuterDisplays(loopWalls, baseWalls, rooms),
  };
};

const solveSegmentFromLabelLine = (
  labelStartInput: Vector3,
  labelEndInput: Vector3,
  depth: number,
  startDockCenter?: Vector3 | null,
  endDockCenter?: Vector3 | null,
  existingInnerSnapWalls: StandardWallModel[] = [],
): SolvedPreviewSegment => {
  const baseNormal = getLeftNormal(labelStartInput, labelEndInput, depth);

  if (!startDockCenter && !endDockCenter) {
    return {
      labelStart: labelStartInput.clone(),
      labelEnd: labelEndInput.clone(),
      centerStart: labelStartInput.clone().add(baseNormal),
      centerEnd: labelEndInput.clone().add(baseNormal),
    };
  }

  const startInnerHit = startDockCenter ? findNearestExistingInnerPoint(startDockCenter, existingInnerSnapWalls) : null;
  const endInnerHit = endDockCenter ? findNearestExistingInnerPoint(endDockCenter, existingInnerSnapWalls) : null;

  const startInnerPoint = startInnerHit?.point ?? null;
  const endInnerPoint = endInnerHit?.point ?? null;

  let effectiveNormal = baseNormal.clone();

  if (startDockCenter && startInnerPoint) {
    const dockOffset = startDockCenter.clone().sub(startInnerPoint);

    if (dockOffset.lengthSq() > ZERO_LENGTH_EPS) {
      effectiveNormal = dockOffset;
    }
  } else if (startDockCenter && endDockCenter && endInnerPoint) {
    const dockOffset = endDockCenter.clone().sub(endInnerPoint);

    if (dockOffset.lengthSq() > ZERO_LENGTH_EPS) {
      effectiveNormal = dockOffset;
    }
  } else if (startDockCenter || endDockCenter) {
    const dockCenter = startDockCenter ?? endDockCenter;
    const dockInput = startDockCenter ? labelStartInput : labelEndInput;

    if (dockCenter) {
      const labelUsingBaseNormal = dockCenter.clone().sub(baseNormal);
      const labelUsingFlippedNormal = dockCenter.clone().add(baseNormal);

      const baseDistance = labelUsingBaseNormal.distanceTo(dockInput);
      const flippedDistance = labelUsingFlippedNormal.distanceTo(dockInput);

      effectiveNormal = baseDistance <= flippedDistance ? baseNormal.clone() : baseNormal.clone().multiplyScalar(-1);
    }
  }

  const labelStart = startDockCenter ? startInnerPoint?.clone() ?? startDockCenter.clone().sub(effectiveNormal) : labelStartInput.clone();
  const labelEnd = endDockCenter ? endInnerPoint?.clone() ?? endDockCenter.clone().sub(effectiveNormal) : labelEndInput.clone();

  const centerStart = startDockCenter ? startDockCenter.clone() : labelStart.clone().add(effectiveNormal);
  const centerEnd = endDockCenter ? endDockCenter.clone() : labelEnd.clone().add(effectiveNormal);

  return {
    labelStart,
    labelEnd,
    centerStart,
    centerEnd,
  };
};

const createStandardFromPreview = (preview: PreviewWallModel, attrs: any[], unitType: any): StandardWallModel => {
  const wall = new StandardWallModel(preview.previewStartPoint.clone(), preview.previewEndPoint.clone(), preview.depth, [], [], [], [], preview.uuid);

  if (attrs.length > 0) {
    wall.attributes = updateWallGeometryAttributes(wall, unitType, cloneAttrs(attrs));
  }

  if (typeof (wall as any).updateWallLengths === 'function') {
    (wall as any).updateWallLengths();
  }

  return wall;
};

type PreviewWallWithDisplayLabel = PreviewWallModel & {
  displayLabelStartPoint?: unknown;
  displayLabelEndPoint?: unknown;
};

const getDisplayLabelStartPoint = (preview: PreviewWallModel): Vector3 => {
  const displayStartPoint = (preview as PreviewWallWithDisplayLabel).displayLabelStartPoint;

  return displayStartPoint instanceof Vector3 ? displayStartPoint.clone() : preview.labelStartPoint.clone();
};

const getDisplayLabelEndPoint = (preview: PreviewWallModel): Vector3 => {
  const displayEndPoint = (preview as PreviewWallWithDisplayLabel).displayLabelEndPoint;

  return displayEndPoint instanceof Vector3 ? displayEndPoint.clone() : preview.labelEndPoint.clone();
};

const isAdjacentPreviewValidationConflictAllowed = (previous: PreviewWallModel, candidate: PreviewWallModel): boolean => {
  const structuralConnection =
    previous.structuralEndPoint.distanceTo(candidate.structuralStartPoint) <= ROOM_COMPLETION_TOLERANCE ||
    previous.previewEndPoint.distanceTo(candidate.previewStartPoint) <= ROOM_COMPLETION_TOLERANCE ||
    getDisplayLabelEndPoint(previous).distanceTo(getDisplayLabelStartPoint(candidate)) <= ROOM_COMPLETION_TOLERANCE;

  if (!structuralConnection) return false;

  const previousStart = getDisplayLabelStartPoint(previous);
  const previousEnd = getDisplayLabelEndPoint(previous);
  const candidateStart = getDisplayLabelStartPoint(candidate);
  const candidateEnd = getDisplayLabelEndPoint(candidate);

  if (previousStart.distanceTo(previousEnd) <= ZERO_LENGTH_EPS || candidateStart.distanceTo(candidateEnd) <= ZERO_LENGTH_EPS) return true;

  const previousStart2 = p2(previousStart);
  const previousEnd2 = p2(previousEnd);
  const candidateStart2 = p2(candidateStart);
  const candidateEnd2 = p2(candidateEnd);
  const isCollinear =
    Math.abs(cross(previousStart2, previousEnd2, candidateStart2)) <= EPS_I && Math.abs(cross(previousStart2, previousEnd2, candidateEnd2)) <= EPS_I;

  if (isCollinear) {
    return colinearOverlapLength(previousStart2, previousEnd2, candidateStart2, candidateEnd2) <= EPS_I;
  }

  const intersection = lineIntersection3(previousStart, previousEnd, candidateStart, candidateEnd);
  if (!intersection) return false;

  const onPrevious = closestPointOnSegment(previousStart, previousEnd, intersection).distanceTo(intersection) <= EPS_I;
  const onCandidate = closestPointOnSegment(candidateStart, candidateEnd, intersection).distanceTo(intersection) <= EPS_I;

  if (!onPrevious || !onCandidate) return false;

  const maxConnectionDistance = Math.max(DOCK_POINT_TOLERANCE * 3, previous.depth * 3, candidate.depth * 3);
  const connectionPoints = [
    previous.structuralEndPoint,
    candidate.structuralStartPoint,
    previous.previewEndPoint,
    candidate.previewStartPoint,
    previousEnd,
    candidateStart,
  ];

  return connectionPoints.some((point) => point.distanceTo(intersection) <= maxConnectionDistance);
};

const createLabelStandardFromPreview = (preview: PreviewWallModel): StandardWallModel =>
  new StandardWallModel(getDisplayLabelStartPoint(preview), getDisplayLabelEndPoint(preview), preview.depth, [], [], [], [], preview.uuid);

const copyPreviewDisplayState = (target: PreviewWallModel, source: PreviewWallModel): PreviewWallModel => {
  const targetMeta = target as any;
  const sourceMeta = source as any;
  const vectorDisplayKeys = [
    'displayLabelStartPoint',
    'displayLabelEndPoint',
    'displayInnerLabelStartPoint',
    'displayInnerLabelEndPoint',
    'displayOuterCenterStartPoint',
    'displayOuterCenterEndPoint',
    'displayOuterStartPoint',
    'displayOuterEndPoint',
  ];

  vectorDisplayKeys.forEach((key) => {
    if (sourceMeta[key] instanceof Vector3) {
      targetMeta[key] = sourceMeta[key].clone();
    } else {
      delete targetMeta[key];
    }
  });

  ['applyDockDisplayLength', 'hideLengthIndicators', 'projectedDockAxisStart'].forEach((key) => {
    if (sourceMeta[key] !== undefined) targetMeta[key] = sourceMeta[key];
    else delete targetMeta[key];
  });

  return target;
};

export const useWallManagement = (
  drawEnabled: boolean,
  isDrawing: boolean,
  setIsDrawing: (isDrawing: boolean) => void,
  roomCompleted: boolean,
  setRoomCompleted: (roomCompleted: boolean) => void,
  onLoopClosed?: LoopClosedHandler,
) => {
  const { isSnappingEnabled } = useMultiSelectToggleContext();
  const { hasIntersectingWalls, setHasIntersectingWalls } = useValidation();

  const [tempWall, setTempWall] = useState<IWall>();
  const [previewWalls, setPreviewWalls] = useState<IWall[]>([]);
  const [startPoint, setStartPoint] = useState<Vector3 | null>(null);
  const [snapLines, setSnapLines] = useState<SnapLineProps[]>([]);

  const tempWallRef = useRef<IWall | null>(null);
  const chainStartPointRef = useRef<Vector3 | null>(null);
  const chainPreviewWallsRef = useRef<PreviewWallModel[]>([]);
  const baseWallAttributesRef = useRef<any[]>([]);
  const lastSnapInfoRef = useRef<SnapInfo>({ kind: 'none', point: null });
  const currentSegmentStartDockCenterRef = useRef<Vector3 | null>(null);

  const { building, setBuildingWalls } = useBuilding();
  const { unitType } = useUnitContext();

  const lastSnappedPointRef = useRef<Vector3 | null>(null);
  const { showSnackbar } = useSnackbar();
  const intl = useIntl();
  const checkDoubleClick = useDoubleClickDetection();

  const { planModel } = usePlan();
  const regionCode = planModel?.region ? String(planModel.region).toUpperCase() : 'DE';

  const isCloseEnough = useCallback((p1: Vector3, p2: Vector3) => Math.hypot(p1.x - p2.x, p1.y - p2.y) <= ROOM_COMPLETION_TOLERANCE, []);

  const getThickness = useCallback(() => {
    const last = chainPreviewWallsRef.current[chainPreviewWallsRef.current.length - 1];
    return last?.depth ?? DEFAULT_WALL_DEPTH;
  }, []);

  const getDockPointNear = useCallback(
    (x: number, y: number, tolerance = DOCK_POINT_TOLERANCE): Vector3 | null => {
      let bestPoint: Vector3 | null = null;
      let bestDistance = tolerance;

      building.walls.forEach((w) => {
        if (!w || w.type !== EWall.STANDARD) return;

        [w.startPoint, w.endPoint].forEach((p) => {
          const distance = Math.hypot(p.x - x, p.y - y);

          if (distance <= bestDistance) {
            bestDistance = distance;
            bestPoint = p.clone();
            bestPoint.z = 0;
          }
        });
      });

      return bestPoint;
    },
    [building.walls],
  );

  const getExistingRoomInnerSnapWalls = useCallback((): StandardWallModel[] => {
    const snapWalls: StandardWallModel[] = [];

    (building.rooms ?? []).forEach((room: any) => {
      (room.spaces ?? []).forEach((space: any) => {
        (space.boundaries ?? []).forEach((boundary: any) => {
          const edges = boundary?.edges;

          if (!Array.isArray(edges)) return;

          const boundaryEdges = edges.filter(Boolean) as IBoundaryEdge[];
          const orderedEdges = orderBoundaryEdges(boundaryEdges);
          const polygonOrientation = calculateBoundaryPolygonOrientation(orderedEdges);
          const insetSegments = calculateBoundaryIndicatorSegments(orderedEdges, polygonOrientation);

          if (insetSegments.length === orderedEdges.length) {
            insetSegments.forEach(({ edge, startPoint: s, endPoint: e }) => {
              const availableSideStart = getAvailableSidePointForExistingBoundary(s, edge);
              const availableSideEnd = getAvailableSidePointForExistingBoundary(e, edge);

              if (availableSideStart.distanceTo(availableSideEnd) <= ZERO_LENGTH_EPS) return;

              const snapWall = new StandardWallModel(availableSideStart, availableSideEnd, edge.wall?.depth ?? DEFAULT_WALL_DEPTH);

              setSnapWallSource(snapWall, edge.wall);
              snapWalls.push(snapWall);
            });
            return;
          }

          boundaryEdges.forEach((edge: any) => {
            const s = edge?.startPoint;
            const e = edge?.endPoint;

            if (!(s instanceof Vector3) || !(e instanceof Vector3)) return;

            const availableSideStart = getAvailableSidePointForExistingBoundary(s, edge);
            const availableSideEnd = getAvailableSidePointForExistingBoundary(e, edge);

            if (availableSideStart.distanceTo(availableSideEnd) <= ZERO_LENGTH_EPS) return;

            const snapWall = new StandardWallModel(availableSideStart, availableSideEnd, edge.wall?.depth ?? DEFAULT_WALL_DEPTH);

            setSnapWallSource(snapWall, edge.wall);
            snapWalls.push(snapWall);
          });
        });
      });
    });

    return snapWalls;
  }, [building.rooms]);

  const createPreviewFromSolved = useCallback(
    (
      structuralStart: Vector3,
      structuralEnd: Vector3,
      labelStart: Vector3,
      labelEnd: Vector3,
      centerStart: Vector3,
      centerEnd: Vector3,
      depth: number,
      uuid?: string,
      startDockCenter?: Vector3 | null,
      endDockCenter?: Vector3 | null,
    ): PreviewWallModel => {
      const temporaryShapeWalls = [
        ...building.walls,
        ...chainPreviewWallsRef.current.map((preview) => createStandardFromPreview(preview, [], unitType)),
        new StandardWallModel(centerStart.clone(), centerEnd.clone(), depth),
      ];

      const shapeIndex = temporaryShapeWalls.length - 1;
      const geometry = centerStart.distanceTo(centerEnd) < ZERO_LENGTH_EPS ? undefined : calculateWallShape(centerStart, centerEnd, depth, temporaryShapeWalls, shapeIndex);

      const preview = new PreviewWallModel({
        uuid,
        structuralStartPoint: structuralStart.clone(),
        structuralEndPoint: structuralEnd.clone(),
        previewStartPoint: centerStart.clone(),
        previewEndPoint: centerEnd.clone(),
        depth,
        geometry,
        labelStartPoint: labelStart.clone(),
        labelEndPoint: labelEnd.clone(),
      });

      (preview as any).startDockCenter = startDockCenter?.clone() ?? null;
      (preview as any).endDockCenter = endDockCenter?.clone() ?? null;
      (preview as any).applyDockDisplayLength = true;
      (preview as any).hideLengthIndicators = false;

      return preview;
    },
    [building.walls, unitType],
  );

  const createPreviewForSegment = useCallback(
    (labelStart: Vector3, labelEnd: Vector3, depth: number, startDockCenter?: Vector3 | null, endDockCenter?: Vector3 | null, uuid?: string): PreviewWallModel => {
      const existingInnerSnapWalls = getExistingRoomInnerSnapWalls();
      const solved = solveSegmentFromLabelLine(labelStart, labelEnd, depth, startDockCenter, endDockCenter, existingInnerSnapWalls);

      return createPreviewFromSolved(labelStart, labelEnd, solved.labelStart, solved.labelEnd, solved.centerStart, solved.centerEnd, depth, uuid, startDockCenter, endDockCenter);
    },
    [createPreviewFromSolved, getExistingRoomInnerSnapWalls],
  );

  const buildPreviewModels = useCallback(
    (committed: PreviewWallModel[], hovering?: PreviewWallModel | null): PreviewWallModel[] => {
      const allPreviews = hovering ? [...committed, hovering] : committed.slice();
      if (allPreviews.length === 0) return [];

      const existingInnerSnapWalls = getExistingRoomInnerSnapWalls();

      const solved: SolvedEntry[] = allPreviews.map((preview) => {
        const startDockCenter = (preview as any).startDockCenter instanceof Vector3 ? ((preview as any).startDockCenter as Vector3).clone() : null;
        const endDockCenter = (preview as any).endDockCenter instanceof Vector3 ? ((preview as any).endDockCenter as Vector3).clone() : null;

        const solvedSegment = solveSegmentFromLabelLine(
          preview.structuralStartPoint,
          preview.structuralEndPoint,
          preview.depth,
          startDockCenter,
          endDockCenter,
          existingInnerSnapWalls,
        );

        return {
          preview,
          structuralStart: preview.structuralStartPoint.clone(),
          structuralEnd: preview.structuralEndPoint.clone(),
          labelStart: solvedSegment.labelStart,
          labelEnd: solvedSegment.labelEnd,
          centerStart: solvedSegment.centerStart,
          centerEnd: solvedSegment.centerEnd,
          startDockCenter,
          endDockCenter,
          startLockedToDock: !!startDockCenter,
          endLockedToDock: !!endDockCenter,
        };
      });

      for (let i = 1; i < solved.length; i++) {
        const prev = solved[i - 1];
        const curr = solved[i];

        if (!isCloseEnough(prev.labelEnd, curr.labelStart)) continue;

        const inter = lineIntersection3(prev.centerStart, prev.centerEnd, curr.centerStart, curr.centerEnd);
        if (!inter) continue;

        if (!prev.endLockedToDock) prev.centerEnd = inter.clone();
        if (!curr.startLockedToDock) curr.centerStart = inter.clone();
      }

      const first = solved[0];
      const last = solved[solved.length - 1];
      const isLabelClosed = solved.length >= 3 && isCloseEnough(last.labelEnd, first.labelStart);
      const isClosed =
        isLabelClosed &&
        (!first.startDockCenter || !last.endDockCenter || isCloseEnough(first.startDockCenter, last.endDockCenter) || isCloseEnough(first.centerStart, last.centerEnd));

      if (isClosed) {
        const inter = lineIntersection3(last.centerStart, last.centerEnd, first.centerStart, first.centerEnd);

        if (inter) {
          if (!last.endLockedToDock) last.centerEnd = inter.clone();
          if (!first.startLockedToDock) first.centerStart = inter.clone();
        }
      }

      let projectedClosureConnectorWall: StandardWallModel | null = null;
      const sharedClosureWalls = (() => {
        if (isClosed || solved.length === 0) return null;

        const firstEntry = solved[0];
        const lastEntry = solved[solved.length - 1];
        const findDockAround = (...points: Vector3[]) => {
          for (const point of points) {
            const dock = getDockPointNear(point.x, point.y, ROOM_COMPLETION_TOLERANCE);

            if (dock) return dock;
          }

          return null;
        };
        const firstDockCenter = firstEntry.startDockCenter ?? findDockAround(firstEntry.labelStart, firstEntry.structuralStart, firstEntry.centerStart);
        const directLastDockCenter = lastEntry.endDockCenter ?? findDockAround(lastEntry.labelEnd, lastEntry.structuralEnd, lastEntry.centerEnd);

        const findProjectedClosure = () => {
          if (!firstDockCenter || directLastDockCenter || solved.length < 2) return null;

          const openEnds = [lastEntry.labelEnd, lastEntry.structuralEnd, lastEntry.centerEnd];
          const maxAxisMiss = Math.max(ROOM_COMPLETION_TOLERANCE, lastEntry.preview.depth * 0.75);
          let best: { dock: Vector3; path: StandardWallModel[]; score: number } | null = null;

          building.walls.forEach((wall) => {
            if (!wall || wall.type !== EWall.STANDARD) return;

            [wall.startPoint, wall.endPoint].forEach((candidatePoint) => {
              const candidate = candidatePoint.clone();

              if (candidate.distanceTo(firstDockCenter) <= ROOM_COMPLETION_TOLERANCE) return;

              const alignedOpenEnd = openEnds.reduce<{ point: Vector3; axisMiss: number } | null>((bestOpenEnd, openEnd) => {
                if (candidate.distanceTo(openEnd) <= ROOM_COMPLETION_TOLERANCE) return bestOpenEnd;

                const dx = Math.abs(candidate.x - openEnd.x);
                const dy = Math.abs(candidate.y - openEnd.y);
                const axisMiss = Math.min(dx, dy);

                if (axisMiss > maxAxisMiss) return bestOpenEnd;
                if (candidate.distanceTo(openEnd) > MAX_PROJECTED_PREVIEW_CLOSURE_DISTANCE) return bestOpenEnd;
                if (!bestOpenEnd || axisMiss < bestOpenEnd.axisMiss) return { point: openEnd, axisMiss };

                return bestOpenEnd;
              }, null);

              if (!alignedOpenEnd) return;

              const drawnPathPoints = [firstEntry.centerStart.clone(), ...solved.slice(0, -1).map((entry) => entry.centerEnd.clone()), candidate.clone()];
              const path = findPathOverExistingWalls(candidate, firstDockCenter, building.walls, lastEntry.centerStart, drawnPathPoints);
              if (!path || path.length === 0) return;

              const pathLength = path.reduce((sum, pathWall) => sum + pathWall.startPoint.distanceTo(pathWall.endPoint), 0);
              const score = alignedOpenEnd.axisMiss * 1000 + candidate.distanceTo(alignedOpenEnd.point) + pathLength * 0.01;

              if (!best || score < best.score) {
                best = {
                  dock: candidate,
                  path,
                  score,
                };
              }
            });
          });

          return best;
        };
        const projectedClosure = findProjectedClosure();
        const lastDockCenter = directLastDockCenter ?? projectedClosure?.dock ?? null;

        if (!firstDockCenter || !lastDockCenter) return null;
        if (isCloseEnough(firstDockCenter, lastDockCenter)) return null;

        const activeIndex = solved.length - 1;
        const committedPrevious = committed[activeIndex - 1];
        const closingIncomingStart =
          hovering && lastEntry.endDockCenter && !lastEntry.startDockCenter && PreviewWallModel.isPreviewWall(committedPrevious)
            ? committedPrevious.previewEndPoint.clone()
            : lastEntry.centerStart;
        const drawnPathPoints = [firstEntry.centerStart.clone(), ...solved.map((entry) => entry.centerEnd.clone())];

        if (!isCloseEnough(drawnPathPoints[drawnPathPoints.length - 1], lastDockCenter)) {
          drawnPathPoints.push(lastDockCenter.clone());
        }

        const path = projectedClosure?.path ?? findPathOverExistingWalls(lastDockCenter, firstDockCenter, building.walls, closingIncomingStart, drawnPathPoints);
        if (!path || path.length === 0) return null;

        if (projectedClosure && lastEntry.centerEnd.distanceTo(lastDockCenter) > ZERO_LENGTH_EPS) {
          projectedClosureConnectorWall = new StandardWallModel(lastEntry.centerEnd.clone(), lastDockCenter.clone(), lastEntry.preview.depth);
        }

        path[0].startPoint = lastDockCenter.clone();
        path[path.length - 1].endPoint = firstDockCenter.clone();
        path.forEach((wall) => wall.updateWallLengths?.());

        return path;
      })();

      const activeAxisAlignedEndDockStart = (() => {
        if (!hovering || isClosed || solved.length < 2) return null;

        const active = solved[solved.length - 1];
        const previous = solved[solved.length - 2];
        if (!active.endDockCenter || active.startDockCenter) return null;

        const dock = active.endDockCenter;
        const activeStructuralDirection = active.structuralEnd.clone().sub(active.structuralStart);
        const activeIsHorizontal = Math.abs(activeStructuralDirection.y) <= EPS_I && Math.abs(activeStructuralDirection.x) > ZERO_LENGTH_EPS;
        const activeIsVertical = Math.abs(activeStructuralDirection.x) <= EPS_I && Math.abs(activeStructuralDirection.y) > ZERO_LENGTH_EPS;
        const previousStructuralDirection = previous.structuralEnd.clone().sub(previous.structuralStart);
        const previousLength = previousStructuralDirection.length();
        const maxPreviousAxisMiss =
          previousLength > ZERO_LENGTH_EPS
            ? Math.max(Math.sin((SNAP_ANGLE_THRESHOLD * Math.PI) / HALF_CIRCLE_DEGREES) * previousLength, previous.preview.depth / 2 + EPS_I)
            : 0;
        const previousIsNearlyVertical = previousLength > ZERO_LENGTH_EPS && Math.abs(previousStructuralDirection.x) <= maxPreviousAxisMiss;
        const previousIsNearlyHorizontal = previousLength > ZERO_LENGTH_EPS && Math.abs(previousStructuralDirection.y) <= maxPreviousAxisMiss;
        const previousIsPerpendicularAxis =
          (activeIsHorizontal && previousIsNearlyVertical) || (activeIsVertical && previousIsNearlyHorizontal);

        if (previousIsPerpendicularAxis) {
          const projectedStart = active.centerStart.clone();

          if (activeIsHorizontal) {
            projectedStart.y = dock.y;
          } else {
            projectedStart.x = dock.x;
          }

          const projectionDistance = projectedStart.distanceTo(active.centerStart);
          const maxProjectionDistance = Math.max(DOCK_POINT_TOLERANCE * 3, active.preview.depth * 1.5);

          if (projectionDistance > EPS_I && projectionDistance <= maxProjectionDistance) {
            return projectedStart;
          }
        }

        if (!sharedClosureWalls?.[0]) return null;

        const firstEntry = solved[0];
        const sharedDirection = sharedClosureWalls[0].endPoint.clone().sub(sharedClosureWalls[0].startPoint);
        const firstDirection = firstEntry.structuralEnd.clone().sub(firstEntry.structuralStart);

        if (!areDirectionsParallel2D(firstDirection, sharedDirection, 0.05)) return null;

        const projectedStart = getDockAxisProjectedStart(active.structuralStart, dock, sharedClosureWalls[0].startPoint, sharedClosureWalls[0].endPoint, active.preview.depth);
        if (!projectedStart) return null;

        return arePointsOnOppositeAxisExtensions(firstEntry.structuralEnd, projectedStart, sharedClosureWalls[0].startPoint, sharedClosureWalls[0].endPoint)
          ? projectedStart
          : null;
      })();
      const activeEndDockFixedStartCenter = (() => {
        if (!hovering || isClosed || solved.length < 2) return null;

        const activeIndex = solved.length - 1;
        const active = solved[activeIndex];
        const committedPrevious = committed[activeIndex - 1];

        if (!active.endDockCenter || active.startDockCenter) return null;
        if (!PreviewWallModel.isPreviewWall(committedPrevious)) return null;

        return committedPrevious.previewEndPoint.clone();
      })();
      const shouldFreezeActiveEndDockPrevious = !!activeEndDockFixedStartCenter && !activeAxisAlignedEndDockStart;

      if (activeAxisAlignedEndDockStart) {
        const activeIndex = solved.length - 1;
        const active = solved[activeIndex];
        const previous = solved[activeIndex - 1];

        active.centerStart = activeAxisAlignedEndDockStart.clone();

        if (previous && !previous.endLockedToDock) {
          previous.centerEnd = activeAxisAlignedEndDockStart.clone();
        }
      }

      const insetLines = solved.map((entry) => {
        const inwardNormal = getRightNormal(entry.centerStart, entry.centerEnd, entry.preview.depth);

        return {
          start: entry.centerStart.clone().add(inwardNormal),
          end: entry.centerEnd.clone().add(inwardNormal),
        };
      });

      const displaySegments = buildDisplaySegmentsForInsetLines(solved, insetLines, existingInnerSnapWalls, isCloseEnough, isClosed, building.walls);
      const activeOpenEndDockLocal = (() => {
        if (!hovering || isClosed || solved.length === 0) return null;

        const activeIndex = solved.length - 1;
        const active = solved[activeIndex];

        if (!active.endDockCenter) return null;

        const activeWithoutEndDock = solveSegmentFromLabelLine(
          active.structuralStart,
          active.structuralEnd,
          active.preview.depth,
          active.startDockCenter,
          null,
          existingInnerSnapWalls,
        );
        const locallySolved = solved.map((entry, index) => ({
          ...entry,
          structuralStart: entry.structuralStart.clone(),
          structuralEnd: entry.structuralEnd.clone(),
          labelStart: index === activeIndex ? activeWithoutEndDock.labelStart.clone() : entry.labelStart.clone(),
          labelEnd: index === activeIndex ? activeWithoutEndDock.labelEnd.clone() : entry.labelEnd.clone(),
          centerStart: index === activeIndex ? activeWithoutEndDock.centerStart.clone() : entry.centerStart.clone(),
          centerEnd: index === activeIndex ? activeWithoutEndDock.centerEnd.clone() : entry.centerEnd.clone(),
          startDockCenter: entry.startDockCenter?.clone() ?? null,
          endDockCenter: index === activeIndex ? null : entry.endDockCenter?.clone() ?? null,
          endLockedToDock: index === activeIndex ? false : entry.endLockedToDock,
        }));

        for (let i = 1; i < locallySolved.length; i++) {
          const prev = locallySolved[i - 1];
          const curr = locallySolved[i];

          if (!isCloseEnough(prev.labelEnd, curr.labelStart)) continue;

          const inter = lineIntersection3(prev.centerStart, prev.centerEnd, curr.centerStart, curr.centerEnd);
          if (!inter) continue;

          if (!prev.endLockedToDock) prev.centerEnd = inter.clone();
          if (!curr.startLockedToDock) curr.centerStart = inter.clone();
        }

        if (activeAxisAlignedEndDockStart) {
          locallySolved[activeIndex].centerStart = activeAxisAlignedEndDockStart.clone();
        } else if (activeEndDockFixedStartCenter) {
          locallySolved[activeIndex].centerStart = activeEndDockFixedStartCenter.clone();
        }

        const localInsetLines = locallySolved.map((entry) => {
          const inwardNormal = getRightNormal(entry.centerStart, entry.centerEnd, entry.preview.depth);

          return {
            start: entry.centerStart.clone().add(inwardNormal),
            end: entry.centerEnd.clone().add(inwardNormal),
          };
        });

        return {
          centerStart: locallySolved[activeIndex].centerStart.clone(),
          displaySegment: buildDisplaySegmentsForInsetLines(locallySolved, localInsetLines, existingInnerSnapWalls, isCloseEnough, false, building.walls)[activeIndex],
        };
      })();
      const previewStandards = solved.map(
        (entry) => new StandardWallModel(entry.centerStart.clone(), entry.centerEnd.clone(), entry.preview.depth, [], [], [], [], entry.preview.uuid),
      );
      const finalSimulationPreviewStandards =
        activeOpenEndDockLocal || shouldFreezeActiveEndDockPrevious
          ? solved.map((entry, idx) => {
              const frozenPreview =
                shouldFreezeActiveEndDockPrevious && idx === solved.length - 2 && PreviewWallModel.isPreviewWall(entry.preview) ? (entry.preview as PreviewWallModel) : null;
              const centerStart =
                frozenPreview?.previewStartPoint.clone() ?? (idx === solved.length - 1 && activeOpenEndDockLocal ? activeOpenEndDockLocal.centerStart : entry.centerStart);
              const centerEnd = frozenPreview?.previewEndPoint.clone() ?? entry.centerEnd;

              return new StandardWallModel(centerStart.clone(), centerEnd.clone(), entry.preview.depth, [], [], [], [], entry.preview.uuid);
            })
          : previewStandards;

      const projectedClosureConnectorWalls = projectedClosureConnectorWall ? [projectedClosureConnectorWall] : [];
      const simulationLoopWalls = isClosed
        ? finalSimulationPreviewStandards
        : sharedClosureWalls
          ? [...finalSimulationPreviewStandards, ...projectedClosureConnectorWalls, ...sharedClosureWalls]
          : null;
      const hasFinalPreviewLoop =
        simulationLoopWalls && simulationLoopWalls.length >= 3 && buildContinuousEdgesLikeBuildingForPreview(simulationLoopWalls).length === simulationLoopWalls.length;
      const finalPreviewLoopWalls = hasFinalPreviewLoop ? simulationLoopWalls : null;
      const finalPreviewGeometry = finalPreviewLoopWalls ? simulateFinalPreviewGeometry(finalPreviewLoopWalls, building.rooms ?? [], building.walls) : null;
      const finalPreviewOuterDisplays = simulationLoopWalls ? buildFinalPreviewOuterDisplays(simulationLoopWalls, building.walls, building.rooms ?? []) : null;
      const isCounterClockwise = finalPreviewGeometry
        ? finalPreviewGeometry.rawWindingOrder === EWindingOrder.COUNTERCLOCKWISE
        : isCounterClockwisePreviewChain(allPreviews, building.walls);
      const applyDockDisplayLength = !isCounterClockwise;

      const counterClockwiseInnerDisplaySegments = isCounterClockwise
        ? buildDisplaySegmentsForInsetLines(
            solved,
            solved.map((entry) => {
              const inwardNormal = getLeftNormal(entry.centerStart, entry.centerEnd, entry.preview.depth);

              return {
                start: entry.centerStart.clone().add(inwardNormal),
                end: entry.centerEnd.clone().add(inwardNormal),
              };
            }),
            existingInnerSnapWalls,
            isCloseEnough,
            isClosed,
            building.walls,
          )
        : null;

      const wallsForShape: IWall[] = [...building.walls, ...previewStandards];

      const counterClockwiseOuterDisplays: Array<PreviewOuterDisplay | null> | null = isCounterClockwise
        ? (() => {
            const finalDisplays = finalPreviewOuterDisplays ?? finalPreviewGeometry?.outerDisplaysByUuid ?? null;
            const finalPreviewStandards = solved.map(
              (entry) => new StandardWallModel(entry.centerEnd.clone(), entry.centerStart.clone(), entry.preview.depth, [], [], [], [], entry.preview.uuid),
            );
            const finalWallsForShape: IWall[] = [...building.walls, ...finalPreviewStandards];

            return finalPreviewStandards.map((finalWall, idx) => {
              const finalDisplay = finalDisplays?.get(finalWall.uuid);
              if (finalDisplay) return finalDisplay;
              if (finalWall.startPoint.distanceTo(finalWall.endPoint) < ZERO_LENGTH_EPS) return null;

              const shapeIndex = building.walls.length + idx;
              const geometry = calculateWallShape(finalWall.startPoint, finalWall.endPoint, finalWall.depth, finalWallsForShape, shapeIndex);

              return {
                geometry,
                centerStart: finalWall.startPoint.clone(),
                centerEnd: finalWall.endPoint.clone(),
                outerStart: geometry.pointC.clone(),
                outerEnd: geometry.pointD.clone(),
              };
            });
          })()
        : null;

      return solved.map((entry, idx) => {
        const shapeIndex = building.walls.length + idx;
        const displaySegment = displaySegments[idx];
        const isHoveringEntry = !!hovering && idx === solved.length - 1;
        const shouldPreserveActiveStart = isHoveringEntry && !isClosed && !!activeOpenEndDockLocal;
        const shouldFreezePreviousForActiveEndDock = !!hovering && !isClosed && shouldFreezeActiveEndDockPrevious && idx === solved.length - 2;
        const frozenVisualPreview = shouldFreezePreviousForActiveEndDock && PreviewWallModel.isPreviewWall(entry.preview) ? (entry.preview as PreviewWallModel) : null;
        const rawCounterClockwiseOuterDisplay = counterClockwiseOuterDisplays?.[idx] ?? null;
        const finalInsetDisplay = finalPreviewGeometry?.insetSegmentsByUuid.get(entry.preview.uuid) ?? null;
        const rawFinalOuterDisplay = finalPreviewOuterDisplays?.get(entry.preview.uuid) ?? finalPreviewGeometry?.outerDisplaysByUuid.get(entry.preview.uuid) ?? null;
        const counterClockwiseOuterDisplay = rawCounterClockwiseOuterDisplay ? getOuterDisplayForIndicator(rawCounterClockwiseOuterDisplay) : null;
        const finalOuterDisplay = rawFinalOuterDisplay ? getOuterDisplayForIndicator(rawFinalOuterDisplay) : null;
        const activeDisplayLocal = shouldPreserveActiveStart ? activeOpenEndDockLocal : null;
        const activeGeometryLocal = shouldPreserveActiveStart ? activeOpenEndDockLocal : null;
        const frozenDisplaySegment = frozenVisualPreview
          ? {
              start: getDisplayLabelStartPoint(frozenVisualPreview),
              end: getDisplayLabelEndPoint(frozenVisualPreview),
            }
          : null;
        const visualCenterStart = frozenVisualPreview?.previewStartPoint.clone() ?? activeGeometryLocal?.centerStart ?? entry.centerStart;
        const visualCenterEnd = frozenVisualPreview?.previewEndPoint.clone() ?? entry.centerEnd;
        const currentDisplaySegment = activeDisplayLocal?.displaySegment ?? frozenDisplaySegment ?? displaySegment;
        const activeEndDockDisplaySegment =
          shouldPreserveActiveStart && finalInsetDisplay ? displaySegmentWithCurrentStartAndFinalLength(currentDisplaySegment, finalInsetDisplay) : null;
        const primaryDisplaySegment = activeEndDockDisplaySegment ?? finalInsetDisplay ?? frozenDisplaySegment ?? displaySegment;
        const visualPreviewStandards = activeGeometryLocal
          ? previewStandards.map((wall, wallIndex) => {
              if (wallIndex === idx) {
                return new StandardWallModel(visualCenterStart.clone(), visualCenterEnd.clone(), entry.preview.depth, [], [], [], [], entry.preview.uuid);
              }

              if (shouldFreezeActiveEndDockPrevious && wallIndex === solved.length - 2 && PreviewWallModel.isPreviewWall(solved[wallIndex].preview)) {
                const frozenPrevious = solved[wallIndex].preview as PreviewWallModel;

                return new StandardWallModel(
                  frozenPrevious.previewStartPoint.clone(),
                  frozenPrevious.previewEndPoint.clone(),
                  frozenPrevious.depth,
                  [],
                  [],
                  [],
                  [],
                  frozenPrevious.uuid,
                );
              }

              return wall;
            })
          : previewStandards;
        const visualWallsForShape: IWall[] = activeGeometryLocal ? [...building.walls, ...visualPreviewStandards] : wallsForShape;
        const frozenGeometry = frozenVisualPreview
          ? {
              pointC: frozenVisualPreview.pointC.clone(),
              pointD: frozenVisualPreview.pointD.clone(),
              pointC2: frozenVisualPreview.pointC2.clone(),
              pointD2: frozenVisualPreview.pointD2.clone(),
            }
          : null;

        const geometry =
          frozenGeometry ??
          counterClockwiseOuterDisplay?.geometry ??
          (visualCenterStart.distanceTo(visualCenterEnd) < ZERO_LENGTH_EPS
            ? undefined
            : calculateWallShape(visualCenterStart, visualCenterEnd, entry.preview.depth, visualWallsForShape, shapeIndex));

        const rebuilt = new PreviewWallModel({
          uuid: entry.preview.uuid,
          structuralStartPoint: entry.structuralStart,
          structuralEndPoint: entry.structuralEnd,
          previewStartPoint: visualCenterStart.clone(),
          previewEndPoint: visualCenterEnd.clone(),
          depth: entry.preview.depth,
          geometry,
          labelStartPoint: entry.labelStart.clone(),
          labelEndPoint: entry.labelEnd.clone(),
        });

        (rebuilt as any).startDockCenter = entry.startDockCenter?.clone() ?? null;
        (rebuilt as any).endDockCenter = entry.endDockCenter?.clone() ?? null;
        (rebuilt as any).applyDockDisplayLength = frozenVisualPreview ? (frozenVisualPreview as any).applyDockDisplayLength !== false : applyDockDisplayLength;
        (rebuilt as any).hideLengthIndicators = frozenVisualPreview ? (frozenVisualPreview as any).hideLengthIndicators === true : false;
        (rebuilt as any).displayLabelStartPoint = primaryDisplaySegment.start.clone();
        (rebuilt as any).displayLabelEndPoint = primaryDisplaySegment.end.clone();
        (rebuilt as any).projectedDockAxisStart =
          ((entry.preview as any).projectedDockAxisStart === true || (!!activeAxisAlignedEndDockStart && idx === solved.length - 1)) && !!hovering;

        const frozenInnerDisplaySegment =
          frozenVisualPreview &&
          (frozenVisualPreview as any).displayInnerLabelStartPoint instanceof Vector3 &&
          (frozenVisualPreview as any).displayInnerLabelEndPoint instanceof Vector3
            ? {
                start: ((frozenVisualPreview as any).displayInnerLabelStartPoint as Vector3).clone(),
                end: ((frozenVisualPreview as any).displayInnerLabelEndPoint as Vector3).clone(),
              }
            : null;

        if (isCounterClockwise) {
          const innerDisplaySegment = activeEndDockDisplaySegment ?? finalInsetDisplay ?? frozenInnerDisplaySegment ?? counterClockwiseInnerDisplaySegments?.[idx];

          if (innerDisplaySegment) {
            if (finalPreviewGeometry) {
              (rebuilt as any).displayInnerPolygonOrientation = getInnerIndicatorOrientationForDisplay(
                innerDisplaySegment,
                finalPreviewGeometry.polygonOrientation,
                finalOuterDisplay ?? counterClockwiseOuterDisplay,
              );
              (rebuilt as any).displayInnerLabelStartPoint = innerDisplaySegment.start.clone();
              (rebuilt as any).displayInnerLabelEndPoint = innerDisplaySegment.end.clone();
            } else {
              (rebuilt as any).displayInnerLabelStartPoint = innerDisplaySegment.end.clone();
              (rebuilt as any).displayInnerLabelEndPoint = innerDisplaySegment.start.clone();
            }
          }
        } else if (frozenInnerDisplaySegment && !finalInsetDisplay && !activeEndDockDisplaySegment) {
          (rebuilt as any).displayInnerLabelStartPoint = frozenInnerDisplaySegment.start.clone();
          (rebuilt as any).displayInnerLabelEndPoint = frozenInnerDisplaySegment.end.clone();
        }

        if (
          frozenVisualPreview &&
          !finalOuterDisplay &&
          !counterClockwiseOuterDisplay &&
          (frozenVisualPreview as any).displayOuterStartPoint instanceof Vector3 &&
          (frozenVisualPreview as any).displayOuterEndPoint instanceof Vector3
        ) {
          (rebuilt as any).displayOuterCenterStartPoint =
            (frozenVisualPreview as any).displayOuterCenterStartPoint instanceof Vector3
              ? ((frozenVisualPreview as any).displayOuterCenterStartPoint as Vector3).clone()
              : frozenVisualPreview.previewStartPoint.clone();
          (rebuilt as any).displayOuterCenterEndPoint =
            (frozenVisualPreview as any).displayOuterCenterEndPoint instanceof Vector3
              ? ((frozenVisualPreview as any).displayOuterCenterEndPoint as Vector3).clone()
              : frozenVisualPreview.previewEndPoint.clone();
          (rebuilt as any).displayOuterStartPoint = ((frozenVisualPreview as any).displayOuterStartPoint as Vector3).clone();
          (rebuilt as any).displayOuterEndPoint = ((frozenVisualPreview as any).displayOuterEndPoint as Vector3).clone();
        } else if (!isCounterClockwise && finalOuterDisplay) {
          (rebuilt as any).displayOuterCenterStartPoint = finalOuterDisplay.centerStart.clone();
          (rebuilt as any).displayOuterCenterEndPoint = finalOuterDisplay.centerEnd.clone();
          (rebuilt as any).displayOuterStartPoint = finalOuterDisplay.outerStart.clone();
          (rebuilt as any).displayOuterEndPoint = finalOuterDisplay.outerEnd.clone();
        } else if (counterClockwiseOuterDisplay) {
          (rebuilt as any).displayOuterCenterStartPoint = counterClockwiseOuterDisplay.centerStart.clone();
          (rebuilt as any).displayOuterCenterEndPoint = counterClockwiseOuterDisplay.centerEnd.clone();
          (rebuilt as any).displayOuterStartPoint = counterClockwiseOuterDisplay.outerStart.clone();
          (rebuilt as any).displayOuterEndPoint = counterClockwiseOuterDisplay.outerEnd.clone();
        }

        return rebuilt;
      });
    },
    [building.rooms, building.walls, getDockPointNear, getExistingRoomInnerSnapWalls, isCloseEnough],
  );

  const rebuildCommittedPreviews = useCallback(
    (hovering?: PreviewWallModel | null) => {
      const rebuilt = buildPreviewModels(chainPreviewWallsRef.current, hovering ?? null);
      const committedCount = chainPreviewWallsRef.current.length;
      const rebuiltCommitted = rebuilt.slice(0, committedCount);
      const rebuiltHovering = hovering ? rebuilt[committedCount] : null;
      const isClosingHover =
        !!hovering && committedCount >= 2 && chainStartPointRef.current !== null && isCloseEnough(hovering.structuralEndPoint, chainStartPointRef.current);
      const shouldKeepSettledDisplay = !!hovering && !isClosingHover && !((rebuiltHovering as any)?.endDockCenter instanceof Vector3);
      const connectedPreviousIndex =
        shouldKeepSettledDisplay && hovering && committedCount > 0 && PreviewWallModel.isPreviewWall(chainPreviewWallsRef.current[committedCount - 1])
          ? (() => {
              const previous = chainPreviewWallsRef.current[committedCount - 1] as PreviewWallModel;

              return isCloseEnough(previous.labelEndPoint, hovering.labelStartPoint) ? committedCount - 1 : -1;
            })()
          : -1;
      const visibleCommitted = shouldKeepSettledDisplay
        ? rebuiltCommitted.map((preview, index) =>
            index !== connectedPreviousIndex && PreviewWallModel.isPreviewWall(chainPreviewWallsRef.current[index])
              ? copyPreviewDisplayState(preview, chainPreviewWallsRef.current[index])
              : preview,
          )
        : rebuiltCommitted;

      chainPreviewWallsRef.current = visibleCommitted;
      setPreviewWalls(visibleCommitted);

      tempWallRef.current = rebuiltHovering;
      setTempWall(rebuiltHovering ?? undefined);
    },
    [buildPreviewModels, isCloseEnough],
  );

  useEffect(() => {
    if (drawEnabled && baseWallAttributesRef.current.length === 0) {
      const dummyWall = new StandardWallModel(new Vector3(), new Vector3());

      fetchAttributesForObject(dummyWall as any, ObjectType.Wall, regionCode).then((attrs) => {
        baseWallAttributesRef.current = attrs;
      });
    }
  }, [drawEnabled, regionCode]);

  useEffect(() => {
    if (roomCompleted) {
      setSnapLines([]);
      setTempWall(undefined);
      setPreviewWalls([]);
      tempWallRef.current = null;
      chainStartPointRef.current = null;
      chainPreviewWallsRef.current = [];
      lastSnapInfoRef.current = { kind: 'none', point: null };
      currentSegmentStartDockCenterRef.current = null;
    }
  }, [roomCompleted]);

  const handleRoomCompletion = useCallback(
    (endPoint: Vector3): boolean => {
      if (!chainStartPointRef.current) return false;
      return isCloseEnough(endPoint, chainStartPointRef.current);
    },
    [isCloseEnough],
  );

  const cancelAndCleanup = useCallback(() => {
    setSnapLines([]);
    setTempWall(undefined);
    setPreviewWalls([]);
    tempWallRef.current = null;
    setStartPoint(null);
    lastSnappedPointRef.current = null;
    lastSnapInfoRef.current = { kind: 'none', point: null };

    chainPreviewWallsRef.current = [];
    chainStartPointRef.current = null;
    currentSegmentStartDockCenterRef.current = null;

    setHasIntersectingWalls(false);
    setRoomCompleted(false);
    setIsDrawing(false);
  }, [setHasIntersectingWalls, setIsDrawing, setRoomCompleted]);

  useEffect(() => {
    if (!drawEnabled) cancelAndCleanup();
  }, [drawEnabled, cancelAndCleanup]);

  const createWall = useCallback(
    (x: number, y: number, event: ThreeEvent<PointerEvent>): void => {
      if (hasIntersectingWalls) return;
      if (!drawEnabled) return;
      if (event.button !== 0) return;

      setSnapLines([]);

      if (!isDrawing) {
        setRoomCompleted(false);

        const dockCenter = getDockPointNear(x, y);
        const initialPoint = dockCenter ?? new Vector3(x, y, 0);

        chainPreviewWallsRef.current = [];
        setPreviewWalls([]);

        chainStartPointRef.current = initialPoint.clone();
        currentSegmentStartDockCenterRef.current = dockCenter?.clone() ?? null;

        const zeroPreview = createPreviewForSegment(initialPoint, initialPoint, DEFAULT_WALL_DEPTH, dockCenter, dockCenter);

        tempWallRef.current = zeroPreview;
        setStartPoint(initialPoint);
        setTempWall(zeroPreview);
        setHasIntersectingWalls(false);
        setIsDrawing(true);
        return;
      }

      const snapInfo = lastSnapInfoRef.current;
      let endPoint = lastSnappedPointRef.current ? lastSnappedPointRef.current.clone() : new Vector3(x, y, 0);
      let snapKind: SnapKind = snapInfo.kind;
      let snappedDockCenter = snapInfo.dockCenter instanceof Vector3 ? snapInfo.dockCenter.clone() : null;

      lastSnappedPointRef.current = null;
      lastSnapInfoRef.current = { kind: 'none', point: null };

      if (snapKind === 'none') {
        const directDock = getDockPointNear(endPoint.x, endPoint.y, ROOM_COMPLETION_TOLERANCE);

        if (directDock) {
          endPoint = directDock.clone();
          snapKind = chainStartPointRef.current && isCloseEnough(directDock, chainStartPointRef.current) ? 'chainStart' : 'external';
          snappedDockCenter = snapKind === 'external' ? directDock.clone() : null;
        }
      }

      if ((startPoint && startPoint.equals(endPoint)) || checkDoubleClick()) {
        showSnackbar(intl.formatMessage({ id: 'useWallManagement.wallLengthError' }), SnackbarType.ERROR, 3000);
        return;
      }

      let wantsToClose = handleRoomCompletion(endPoint);
      let foundSharedWalls: StandardWallModel[] | null = null;

      const startedFromDock =
        currentSegmentStartDockCenterRef.current instanceof Vector3 || chainPreviewWallsRef.current.some((preview) => (preview as any).startDockCenter instanceof Vector3);

      if (!wantsToClose && chainStartPointRef.current && (chainPreviewWallsRef.current.length > 0 || startedFromDock)) {
        let dockCenter = snapKind === 'external' ? snappedDockCenter?.clone() ?? endPoint.clone() : getDockPointNear(endPoint.x, endPoint.y, ROOM_COMPLETION_TOLERANCE);
        const firstStartDockCenter =
          chainPreviewWallsRef.current[0] && (chainPreviewWallsRef.current[0] as any).startDockCenter instanceof Vector3
            ? ((chainPreviewWallsRef.current[0] as any).startDockCenter as Vector3).clone()
            : null;
        const closureStartPoint = firstStartDockCenter ?? chainStartPointRef.current;

        const closureIncomingStartPoint = PreviewWallModel.isPreviewWall(tempWallRef.current) ? (tempWallRef.current as PreviewWallModel).previewStartPoint.clone() : startPoint!;
        if (dockCenter) {
          const drawnPathPoints = [
            closureStartPoint.clone(),
            ...chainPreviewWallsRef.current.map((preview) => preview.previewEndPoint.clone()),
            dockCenter.clone(),
          ];

          foundSharedWalls = findPathOverExistingWalls(dockCenter, closureStartPoint, building.walls, closureIncomingStartPoint, drawnPathPoints);

          if (foundSharedWalls && foundSharedWalls.length > 0) {
            wantsToClose = true;
            endPoint = closureStartPoint.clone();
            snapKind = 'chainStart';
            foundSharedWalls[foundSharedWalls.length - 1].endPoint = closureStartPoint.clone();
          }
        } else {
          const drawnPathPoints = [closureStartPoint.clone(), ...chainPreviewWallsRef.current.map((preview) => preview.previewEndPoint.clone()), endPoint.clone()];
          const projectedClosure = findProjectedExistingClosure(endPoint, closureStartPoint, building.walls, closureIncomingStartPoint, drawnPathPoints, getThickness());

          if (projectedClosure) {
            dockCenter = projectedClosure.dock.clone();
            foundSharedWalls = projectedClosure.path;
            wantsToClose = true;
            endPoint = closureStartPoint.clone();
            snapKind = 'chainStart';
            foundSharedWalls[0].startPoint = dockCenter.clone();
            foundSharedWalls[foundSharedWalls.length - 1].endPoint = closureStartPoint.clone();
          }
        }
      }

      if (wantsToClose && chainStartPointRef.current && !foundSharedWalls) {
        endPoint = chainStartPointRef.current.clone();
        snapKind = 'chainStart';
      }

      const thickness = getThickness();
      const startDockCenter = currentSegmentStartDockCenterRef.current?.clone() ?? null;

      let endDockCenter: Vector3 | null = null;

      if (snapKind === 'external') {
        endDockCenter = snappedDockCenter?.clone() ?? endPoint.clone();
      } else if (snapKind === 'chainStart' && chainPreviewWallsRef.current.length > 0) {
        endDockCenter =
          (chainPreviewWallsRef.current[0] as any).startDockCenter instanceof Vector3 ? ((chainPreviewWallsRef.current[0] as any).startDockCenter as Vector3).clone() : null;
      }

      const committedEndPoint = foundSharedWalls && foundSharedWalls.length > 0 ? foundSharedWalls[0].startPoint.clone() : endPoint.clone();
      const committedEndDockCenter = foundSharedWalls && foundSharedWalls.length > 0 ? committedEndPoint.clone() : endDockCenter;
      const fallbackPreview = createPreviewForSegment(startPoint!.clone(), committedEndPoint.clone(), thickness, startDockCenter, committedEndDockCenter);

      const tempPreview =
        PreviewWallModel.isPreviewWall(tempWallRef.current) && tempWallRef.current.labelStartPoint.distanceTo(tempWallRef.current.labelEndPoint) > ZERO_LENGTH_EPS
          ? (tempWallRef.current as PreviewWallModel)
          : null;
      const tempStartDockCenter =
        tempPreview && (tempPreview as any).startDockCenter instanceof Vector3 ? (((tempPreview as any).startDockCenter as Vector3).clone() as Vector3) : null;
      const tempEndDockCenter = tempPreview && (tempPreview as any).endDockCenter instanceof Vector3 ? (((tempPreview as any).endDockCenter as Vector3).clone() as Vector3) : null;
      const dockCentersMatch = (a: Vector3 | null, b: Vector3 | null) => {
        if (!a && !b) return true;
        if (!a || !b) return false;

        return a.distanceTo(b) <= EPS_I;
      };
      const forceCommittedPreviewEnd = (preview: PreviewWallModel, forcedEndPoint: Vector3, forcedEndDockCenter: Vector3 | null, allowSharedAxisProjection = true) => {
        const sharedAxisStart = foundSharedWalls?.[0]?.startPoint;
        const sharedAxisEnd = foundSharedWalls?.[0]?.endPoint;
        const firstPreview = chainPreviewWallsRef.current[0];
        const firstDirection =
          PreviewWallModel.isPreviewWall(firstPreview) && sharedAxisStart && sharedAxisEnd ? firstPreview.structuralEndPoint.clone().sub(firstPreview.structuralStartPoint) : null;
        const sharedDirection = sharedAxisStart && sharedAxisEnd ? sharedAxisEnd.clone().sub(sharedAxisStart) : null;
        const canProjectToSharedAxis = allowSharedAxisProjection && !!firstDirection && !!sharedDirection && areDirectionsParallel2D(firstDirection, sharedDirection, 0.05);
        const projectedCandidate =
          canProjectToSharedAxis && sharedAxisStart && sharedAxisEnd
            ? getDockAxisProjectedStart(preview.previewStartPoint, forcedEndPoint, sharedAxisStart, sharedAxisEnd, preview.depth)
            : null;
        const projectedStart =
          projectedCandidate &&
          sharedAxisStart &&
          sharedAxisEnd &&
          PreviewWallModel.isPreviewWall(firstPreview) &&
          arePointsOnOppositeAxisExtensions(firstPreview.structuralEndPoint, projectedCandidate, sharedAxisStart, sharedAxisEnd)
            ? projectedCandidate
            : null;
        const projectionChanged = projectedStart ? projectedStart.distanceTo(preview.previewStartPoint) > EPS_I : false;
        const forcedStartPoint = projectedStart ?? preview.previewStartPoint.clone();
        const forcedWall = new StandardWallModel(forcedStartPoint.clone(), forcedEndPoint.clone(), preview.depth, [], [], [], [], preview.uuid);
        const shapeWalls = [...building.walls, ...chainPreviewWallsRef.current.map((chainPreview) => createStandardFromPreview(chainPreview, [], unitType)), forcedWall];
        const shapeIndex = shapeWalls.length - 1;
        const geometry =
          forcedStartPoint.distanceTo(forcedEndPoint) < ZERO_LENGTH_EPS ? undefined : calculateWallShape(forcedStartPoint, forcedEndPoint, preview.depth, shapeWalls, shapeIndex);
        const rebuilt = new PreviewWallModel({
          uuid: preview.uuid,
          structuralStartPoint: preview.structuralStartPoint.clone(),
          structuralEndPoint: forcedEndPoint.clone(),
          previewStartPoint: forcedStartPoint,
          previewEndPoint: forcedEndPoint.clone(),
          depth: preview.depth,
          geometry,
          labelStartPoint: preview.labelStartPoint.clone(),
          labelEndPoint: preview.labelEndPoint.clone(),
        });

        (rebuilt as any).startDockCenter =
          (preview as any).startDockCenter instanceof Vector3 ? ((preview as any).startDockCenter as Vector3).clone() : startDockCenter?.clone() ?? null;
        (rebuilt as any).endDockCenter = forcedEndDockCenter?.clone() ?? null;
        (rebuilt as any).applyDockDisplayLength = (preview as any).applyDockDisplayLength ?? true;
        (rebuilt as any).hideLengthIndicators = (preview as any).hideLengthIndicators ?? false;
        (rebuilt as any).projectedDockAxisStart = (preview as any).projectedDockAxisStart === true || projectionChanged;

        return rebuilt;
      };
      const tempPreviewMatchesClick =
        !!tempPreview &&
        tempPreview.structuralStartPoint.distanceTo(startPoint!) <= EPS_I &&
        tempPreview.structuralEndPoint.distanceTo(committedEndPoint) <= EPS_I &&
        (!foundSharedWalls || tempPreview.previewEndPoint.distanceTo(committedEndPoint) <= EPS_I) &&
        dockCentersMatch(tempStartDockCenter, startDockCenter) &&
        dockCentersMatch(tempEndDockCenter, committedEndDockCenter);
      const committedPreview =
        foundSharedWalls && tempPreview
          ? forceCommittedPreviewEnd(tempPreview, committedEndPoint, committedEndDockCenter)
          : tempPreviewMatchesClick
            ? tempPreview
            : fallbackPreview;

      const validationStandard = createLabelStandardFromPreview(committedPreview);
      const previousCommittedPreview = chainPreviewWallsRef.current[chainPreviewWallsRef.current.length - 1];
      const committedValidationWalls = chainPreviewWallsRef.current
        .filter(
          (preview) =>
            !(
              previousCommittedPreview &&
              preview.uuid === previousCommittedPreview.uuid &&
              isAdjacentPreviewValidationConflictAllowed(previousCommittedPreview, committedPreview)
            ),
        )
        .map((preview) => createLabelStandardFromPreview(preview));
      const existingPlusCommitted = [...getExistingRoomInnerSnapWalls(), ...committedValidationWalls];

      const isDockingOrClosing = wantsToClose || foundSharedWalls !== null || !!startDockCenter || !!endDockCenter;
      const isValid = isDockingOrClosing ? true : isCandidateValidAgainstExisting(existingPlusCommitted, validationStandard);

      if (!isValid) {
        showSnackbar(intl.formatMessage({ id: 'useWallManagement.wallIntersectionError', defaultMessage: 'Wall intersects. Cannot place.' }), SnackbarType.ERROR, 2500);
        return;
      }

      const wasFirstCommittedWall = chainPreviewWallsRef.current.length === 0;
      const shouldUseSharedClosureSnapshot = wantsToClose && foundSharedWalls !== null;
      const finalPreviewSnapshot = shouldUseSharedClosureSnapshot
        ? (() => {
            const snapshot = [...chainPreviewWallsRef.current, committedPreview];

            if (snapshot.length >= 2 && (committedPreview as any).projectedDockAxisStart === true) {
              const active = snapshot[snapshot.length - 1];
              const previous = snapshot[snapshot.length - 2];

              if (
                PreviewWallModel.isPreviewWall(active) &&
                PreviewWallModel.isPreviewWall(previous) &&
                (active as any).projectedDockAxisStart === true &&
                isCloseEnough(previous.structuralEndPoint, active.structuralStartPoint) &&
                previous.previewEndPoint.distanceTo(active.previewStartPoint) > EPS_I
              ) {
                snapshot[snapshot.length - 2] = forceCommittedPreviewEnd(
                  previous,
                  active.previewStartPoint.clone(),
                  (previous as any).endDockCenter instanceof Vector3 ? ((previous as any).endDockCenter as Vector3).clone() : null,
                  false,
                );
              }
            }

            return snapshot;
          })()
        : null;

      chainPreviewWallsRef.current.push(committedPreview);

      const rebuiltCommitted = finalPreviewSnapshot ?? buildPreviewModels(chainPreviewWallsRef.current);
      chainPreviewWallsRef.current = rebuiltCommitted;
      setPreviewWalls(rebuiltCommitted);

      if (wasFirstCommittedWall && chainPreviewWallsRef.current[0]) {
        chainStartPointRef.current = chainPreviewWallsRef.current[0].labelStartPoint.clone();
      }

      if (wantsToClose) {
        const finalPreviewWalls = finalPreviewSnapshot ?? buildPreviewModels(chainPreviewWallsRef.current, null);
        chainPreviewWallsRef.current = finalPreviewWalls;

        const newStandardWalls = finalPreviewWalls.map((preview) => createStandardFromPreview(preview, baseWallAttributesRef.current, unitType));

        setBuildingWalls((prev) => {
          const existingUuids = new Set((prev ?? []).map((w: any) => w?.uuid));
          const toAdd = newStandardWalls.filter((w) => !existingUuids.has(w.uuid));

          return [...prev, ...toAdd];
        }, EHistoryAction.WALLS_CHANGED);

        setRoomCompleted(true);
        setTempWall(undefined);
        setPreviewWalls([]);
        setIsDrawing(false);
        setStartPoint(null);

        let loopWalls: IStandardWall[] = newStandardWalls;
        if (foundSharedWalls) loopWalls = [...loopWalls, ...foundSharedWalls];

        chainPreviewWallsRef.current = [];
        chainStartPointRef.current = null;
        currentSegmentStartDockCenterRef.current = null;

        onLoopClosed?.(loopWalls);
        return;
      }

      const latestCommitted = chainPreviewWallsRef.current[chainPreviewWallsRef.current.length - 1];
      const nextStartLabelPoint = getDisplayLabelEndPoint(latestCommitted);
      const nextStartDockCenter = (latestCommitted as any).endDockCenter instanceof Vector3 ? ((latestCommitted as any).endDockCenter as Vector3).clone() : null;

      setStartPoint(nextStartLabelPoint);
      currentSegmentStartDockCenterRef.current = nextStartDockCenter;

      const nextZeroPreview = createPreviewForSegment(nextStartLabelPoint, nextStartLabelPoint, thickness, nextStartDockCenter, nextStartDockCenter);
      tempWallRef.current = nextZeroPreview;
      setTempWall(nextZeroPreview);
    },
    [
      building.walls,
      buildPreviewModels,
      checkDoubleClick,
      createPreviewForSegment,
      drawEnabled,
      getDockPointNear,
      getExistingRoomInnerSnapWalls,
      getThickness,
      handleRoomCompletion,
      hasIntersectingWalls,
      intl,
      isDrawing,
      isCloseEnough,
      onLoopClosed,
      setBuildingWalls,
      setHasIntersectingWalls,
      setIsDrawing,
      setRoomCompleted,
      showSnackbar,
      startPoint,
      unitType,
    ],
  );

  const updateWall = useCallback(
    (x: number, y: number): void => {
      if (!drawEnabled || !isDrawing || startPoint === null) return;

      let newSnapLines: any[] = [];
      let snapOccurred = false;
      let snappedPoint = new Vector3(x, y, 0);
      let snappedToChainStart = false;
      let snapKind: SnapKind = 'none';
      let dockCenterForEnd: Vector3 | null = null;

      const applyOrthogonalSnap = (point: Vector3, allowDockAxisProjection = false) => {
        const adjustedPoint = point.clone();
        let angleSnapStartPoint = startPoint.clone();

        if (currentSegmentStartDockCenterRef.current) {
          const thickness = getThickness();
          const existingInnerSnapWalls = getExistingRoomInnerSnapWalls();
          const solved = solveSegmentFromLabelLine(startPoint, adjustedPoint, thickness, currentSegmentStartDockCenterRef.current, null, existingInnerSnapWalls);
          angleSnapStartPoint = solved.labelStart.clone();
        }

        const dx = adjustedPoint.x - angleSnapStartPoint.x;
        const dy = adjustedPoint.y - angleSnapStartPoint.y;

        if (Math.hypot(dx, dy) <= ZERO_LENGTH_EPS) {
          return { point: adjustedPoint, snapped: false };
        }

        const angle = (Math.atan2(dy, dx) * HALF_CIRCLE_DEGREES) / Math.PI;

        if (Math.abs(angle) < SNAP_ANGLE_THRESHOLD || Math.abs(angle) > HALF_CIRCLE_DEGREES - SNAP_ANGLE_THRESHOLD) {
          adjustedPoint.y = angleSnapStartPoint.y;
          return { point: adjustedPoint, snapped: true };
        }

        if (Math.abs(angle - QUARTER_CIRCLE_DEGREES) < SNAP_ANGLE_THRESHOLD || Math.abs(angle + QUARTER_CIRCLE_DEGREES) < SNAP_ANGLE_THRESHOLD) {
          adjustedPoint.x = angleSnapStartPoint.x;
          return { point: adjustedPoint, snapped: true };
        }

        if (allowDockAxisProjection) {
          const axisDistanceTolerance = Math.max(DOCK_POINT_TOLERANCE * 2, getThickness() * 1.5);
          const horizontalDistance = Math.abs(dy);
          const verticalDistance = Math.abs(dx);

          if (horizontalDistance <= axisDistanceTolerance && horizontalDistance <= verticalDistance) {
            adjustedPoint.y = angleSnapStartPoint.y;
            return { point: adjustedPoint, snapped: true };
          }

          if (verticalDistance <= axisDistanceTolerance && verticalDistance < horizontalDistance) {
            adjustedPoint.x = angleSnapStartPoint.x;
            return { point: adjustedPoint, snapped: true };
          }
        }

        return { point: adjustedPoint, snapped: false };
      };

      if (isSnappingEnabled) {
        let dockingMagnet: Vector3 | null = null;
        let bestDist = ROOM_COMPLETION_TOLERANCE;

        if (chainStartPointRef.current) {
          const dist = Math.hypot(x - chainStartPointRef.current.x, y - chainStartPointRef.current.y);

          if (dist <= bestDist) {
            bestDist = dist;
            dockingMagnet = chainStartPointRef.current.clone();
            snappedToChainStart = true;
            snapKind = 'chainStart';
          }
        }

        building.walls.forEach((w) => {
          if (!w || w.type !== EWall.STANDARD) return;

          [w.startPoint, w.endPoint].forEach((p) => {
            const dist = Math.hypot(p.x - x, p.y - y);

            if (dist < bestDist) {
              bestDist = dist;
              dockingMagnet = p.clone();
              dockingMagnet.z = 0;
              snappedToChainStart = false;
              snapKind = 'external';
            }
          });
        });

        if (dockingMagnet) {
          snappedPoint = dockingMagnet.clone();
          dockCenterForEnd = snapKind === 'external' ? dockingMagnet.clone() : null;
          snapOccurred = true;
          setSnapLines([]);

          if (!snappedToChainStart) {
            const orthogonalSnap = applyOrthogonalSnap(snappedPoint, snapKind === 'external');

            if (orthogonalSnap.snapped) {
              snappedPoint = orthogonalSnap.point;
            }
          }
        } else {
          const temporarySnapWalls = [...getExistingRoomInnerSnapWalls(), ...chainPreviewWallsRef.current.map((preview) => createLabelStandardFromPreview(preview))];

          if (temporarySnapWalls.length > 0) {
            ({ snappedPoint, newSnapLines, snapOccurred } = getSnappedPoint(x, y, temporarySnapWalls));

            if (!snapOccurred) {
              const tmp = getFirstWallStartSnapPoint(temporarySnapWalls, x, y);
              const firstWallSnapPoint = new Vector3(x, y, tmp.z);

              if (firstWallSnapPoint.x !== x || firstWallSnapPoint.y !== y) {
                snappedPoint = firstWallSnapPoint;
                snapOccurred = true;
              }
            }

            setSnapLines(newSnapLines);
          } else {
            setSnapLines([]);
          }
        }

        if (!dockingMagnet && !snappedToChainStart) {
          const orthogonalSnap = applyOrthogonalSnap(snappedPoint);

          if (orthogonalSnap.snapped) {
            snappedPoint = orthogonalSnap.point;
            snapOccurred = true;
          }
        }

        lastSnappedPointRef.current = snapOccurred ? snappedPoint.clone() : null;
        lastSnapInfoRef.current = snapOccurred
          ? { kind: snapKind, point: snappedPoint.clone(), dockCenter: snapKind === 'external' ? dockCenterForEnd?.clone() ?? snappedPoint.clone() : null }
          : { kind: 'none', point: null };
      } else {
        lastSnappedPointRef.current = null;
        lastSnapInfoRef.current = { kind: 'none', point: null };
        setSnapLines([]);
      }

      const thickness = getThickness();
      const startDockCenter = currentSegmentStartDockCenterRef.current?.clone() ?? null;
      const endDockCenter = snapKind === 'external' ? dockCenterForEnd?.clone() ?? snappedPoint.clone() : null;

      const hoveringPreview = createPreviewForSegment(startPoint.clone(), snappedPoint.clone(), thickness, startDockCenter, endDockCenter);

      rebuildCommittedPreviews(hoveringPreview);
    },
    [building.walls, createPreviewForSegment, drawEnabled, getExistingRoomInnerSnapWalls, getThickness, isDrawing, isSnappingEnabled, rebuildCommittedPreviews, startPoint],
  );

  return {
    tempWall,
    previewWalls,
    setTempWall,
    snapLines,
    createWall,
    updateWall,
    cancelAndCleanup,
  };
};
